import { CalendarIcon, ChevronDownIcon, ChevronUpIcon, InfoOutlineIcon, TimeIcon } from '@chakra-ui/icons';
import { Box, Button, Card, Checkbox, Collapse, Divider, Flex, Input, InputGroup, InputRightAddon, Link, Radio, RadioGroup, Select, Stack, Text, Textarea, Tooltip, useColorModeValue, useToast } from '@chakra-ui/react';
import AdScheduler from 'components/adspresso/AdScheduler';
import AdvantagePlusAudience from 'components/adspresso/AdvantagePlusAudience';
import AgeGenderSelectorForCampaigns from 'components/adspresso/AgeGenderSelectorForCampaigns';
import AttributeSetting from 'components/adspresso/AttributeSetting';
import BudgetScheduleSpecs from 'components/adspresso/BudgetScheduleSpecs';
import BulkLocationSelector from 'components/adspresso/BulkLocationSelector';
import DetailedTargetingSelector from 'components/adspresso/DetailedTargetingSelector';
import ExcludeCustomAudience from 'components/adspresso/ExcludeCustomAudience';
import ExcludeDevice from 'components/adspresso/ExcludeDevice';
import FaceBookPageAdspressoSelector from 'components/adspresso/FaceBookPageAdspressoSelector';
import GeoLocationsUpdateSelector from 'components/adspresso/GeoLocationsUpdateSelector';
import IncludeCustomAudienceWithBotNullifier from 'components/adspresso/IncludeCustomAudienceWithBotNullifier';
import IncludeDevice from 'components/adspresso/IncludeDevice';
import LocaleUpdateSelector from 'components/adspresso/LocaleUpdateSelector';
import MinuteSecondSpinner from 'components/adspresso/MinuteSecondSpinner';
import OsVersions from 'components/adspresso/OsVersions';
import PixelConversionSelector from 'components/adspresso/PixelConversionSelector';
import PlacementsSelector from 'components/adspresso/PlacmentsSelector';
import WhatsAppSelector from 'components/adspresso/WhatsAppSelector';
import Audience from 'components/campaignForms/AdSetForms/Audience';
import AppEvents from 'components/genericForms/AppEvents';
import AppStore from 'components/genericForms/AppStore';
import BidMultiplier from 'components/genericForms/BidMultuplier';
import currencySymbolMap from 'currency-symbol-map';
import { toZonedTime } from 'date-fns-tz';
import { useFormik } from 'formik';
import countries from 'i18n-iso-countries';
import moment from 'moment';
import 'moment-timezone';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { BsStars } from 'react-icons/bs';
import { SlGraph } from 'react-icons/sl';
import { useCreateAdsetMutation } from 'store/campaigns/adsetFormApi';
import { removeEmptyValues } from 'utils/functions';
import { salesCampaignFormSchema } from 'utils/schema';

countries.registerLocale(require('i18n-iso-countries/langs/en.json'));

function pluralizeType(type) {
    const pluralMapping = {
        country: 'countries',
        city: 'cities',
        region: 'regions',
        zip: 'zips',
        neighborhood: 'neighborhoods',
        geo_market: 'geo_markets',
        electoral_district: 'electoral_districts',
        subcity: 'subcities',
        place: 'places',
        address: 'addresses'
    };
    return pluralMapping[type] || `${type}s`; // Default pluralize rule by appending 's'
}


const transformGeolocations = (locations) => {

    const geoLocations = {
        geo_locations: {}
    };

    // Iterate over each location and group them by their pluralized type
    locations.forEach(location => {
        const pluralizedType = pluralizeType(location.type);

        // Initialize the array for the type if not already initialized
        if (!geoLocations.geo_locations[pluralizedType]) {
            geoLocations.geo_locations[pluralizedType] = [];
        }

        // Push the location to the corresponding type array
        geoLocations.geo_locations[pluralizedType].push(location);
    });

    return geoLocations;
};

const transformExcludeGeolocations = (locations) => {
    // Create the base structure for excluded geo locations
    const excludedGeoLocations = {
        excluded_geo_locations: {}
    };

    // Iterate over each excluded location and group them by their pluralized type
    locations.forEach(location => {
        const pluralizedType = pluralizeType(location.type);

        // Initialize the array for the type if not already initialized
        if (!excludedGeoLocations.excluded_geo_locations[pluralizedType]) {
            excludedGeoLocations.excluded_geo_locations[pluralizedType] = [];
        }

        // Push the excluded location to the corresponding type array
        excludedGeoLocations.excluded_geo_locations[pluralizedType].push(location);
    });

    return excludedGeoLocations;
};


const transformDetailedTargets = (detailedTargets) => {
    const result_object = {};

    detailedTargets?.forEach(item => {
        const { type, id, name } = item;

        if (type === 'relationship_statuses') {
            if (!result_object[type]) {
                result_object[type] = [];
            }
            result_object[type].push(Number(id));
        } else {
            // For other types,
            if (!result_object[type]) {
                result_object[type] = [];
            }
            result_object[type].push({ id, name });
        }
    });

    return {
        flexible_spec: [result_object]
    };
};



const filterUnwantedType = (data) => {
    return Object.fromEntries(
        Object.entries(data).filter(([key, _]) => key !== "location_types")
    );
};


function CreateSalesForm({ data, campaignData, adsAccountData, campaign_id, onClose, onSaveSuccess }) {

    const inputBackground = useColorModeValue('#F5F3F9', '#1e1530');
    const textBackground = useColorModeValue('#F5F3F9', '#1e1530');
    const backgroundColor = useColorModeValue('gray.50', 'gray.800');
    const textColor = useColorModeValue('gray.800', '#fff');
    const borderColor = useColorModeValue('gray.200', 'inherit');
    const hoverBorderColor = useColorModeValue('gray.300', 'gray.700');
    const inputTextColor = useColorModeValue('#421e66', '#b5b5cd')

    const toast = useToast();

    const { objective, buying_type, name: campaignName, bid_strategy: bidStrategy, daily_budget: dailyBudget, lifetime_budget: lifetimeBudget, pacing_type } = campaignData?.[campaign_id] || {};
    const hasBidStrategy = !!bidStrategy;

    const adsAccount = campaignData?.adsAccount;

    const firstAdAccountId = campaignData?.adsAccount?.adsAccountId;

    const timeZone = adsAccountData?.timezone_name;

    let timeZoneAbbreviation = '';

    if (timeZone) {
        timeZoneAbbreviation = moment.tz(timeZone).format('z');
        if (!timeZoneAbbreviation || timeZoneAbbreviation.startsWith('+')) {
            timeZoneAbbreviation = 'UTC';
        }
    } else {
        timeZoneAbbreviation = '';
    }

    const whatsAppNumbersRef = useRef([]);
    const [startTime, setStartTime] = useState(() => {

        // If no start_time is provided, set the default to the current time in the selected timeZone
        if (timeZone) {
            const currentTimeInTimeZone = moment.tz(timeZone).format("HH:mm");
            return currentTimeInTimeZone;
        }

        return "00:00"; // Fallback if no timezone is provided (or any other case)
    });

    const [startDate, setStartDate] = useState(() => {
        const date = new Date();
        return toZonedTime(date, timeZone);
    });

    const [endTime, setEndTime] = useState(() => {
        // If no end_time is provided, set the default to the current time in the selected timeZone
        if (timeZone) {
            const currentEndTimeInTimeZone = moment.tz(timeZone).format("HH:mm");
            return currentEndTimeInTimeZone;
        }

        return "00:00"; // Fallback if no timezone 
    });

    const [endDate, setEndDate] = useState(() => {
        const defaultEndDate = new Date(startDate);
        defaultEndDate.setDate(defaultEndDate.getDate() + 30);
        return toZonedTime(defaultEndDate, timeZone);
    });

    const [errorDateMessage, setErrorDateMessage] = useState('');

    const [showEndDate, setShowEndDate] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isDeviceExpanded, setIsDeviceExpanded] = useState(false);
    const [showExludeDevices, setshowExludeDevices] = useState(false);
    const [placements, setPlacements] = useState({});
    const [showAdvantage, setShowAdvantage] = useState(false);
    const [showExludeAudience, setshowExludeAudience] = useState(false);
    const genderMap = { all: [0], men: [1], women: [2] };
    const [interests, setInterests] = useState([]);
    const [budgetType, setBudgetType] = useState('lifetime_budget');
    const [isScheduled, setIsScheduled] = useState(pacing_type?.includes("day_parting") || false);
    const [adSchedule, setAdSchedule] = useState([]);
    const [timezone, setTimezone] = useState('USER');
    const [zipCodes, setzipCodes] = useState([]);
    const [isDeliveryTypeExpanded, setDeliveryTypeExpanded] = useState(false);
    const [isSchedulingExpanded, setIsSchedulingExpanded] = useState(false);
    const [isBulkLocationSelectorVisible, setisBulkLocationSelectorVisible] = useState(false);
    const currencyCode = adsAccountData?.currency;
    const currencySymbol = currencySymbolMap(currencyCode);
    const [createAdset, { isLoading: isAdsetCreating }] = useCreateAdsetMutation();

    const adSetFormik = useFormik({
        initialValues: {
            name: '',
            destination_type: 'WEBSITE',
            optimization_goal: bidStrategy === 'LOWEST_COST_WITH_MIN_ROAS' ? 'VALUE' : 'OFFSITE_CONVERSIONS',
            promoted_object: [],
            facebookPages: [],
            whatsAppNumbers: [],
            billing_event: 'IMPRESSIONS',
            bid_strategy: bidStrategy ? bidStrategy : 'LOWEST_COST_WITHOUT_CAP',
            bid_amount: '',
            roas_average_floor: '',
            budget: dailyBudget ? 'daily_budget' : 'lifetime_budget',
            daily_budget: dailyBudget ? Number(dailyBudget) / 100 : 800,
            lifetime_budget: lifetimeBudget ? Number(lifetimeBudget) / 100 : 14000,
            is_budget_schedule_enabled: false,
            budget_schedule_specs: [
                {
                  time_start: moment().add(1, 'day').startOf('day').unix(),
                  time_end: moment().add(2, 'day').startOf('day').unix(),
                  budget_value_type: 'ABSOLUTE',
                  budget_value: 5,
                },
            ],
            bid_adjustments: '',
            geo_locations: [],
            excluded_geo_locations: [],
            locales: [],
            ageOriginal: {
                age_min: 18,
                age_max: 65,
            },
            genders: [0],
            ageRange: {
                age_min: 18,
                age_max: 65
            },
            is_incremental_attribution_enabled: false,
            attribution_spec: [
                { event_type: 'CLICK_THROUGH', window_days: 7 },
                { event_type: 'VIEW_THROUGH', window_days: 1 },
                { event_type: 'ENGAGED_VIDEO_VIEW', window_days: 1 },
            ],
            custom_audiences: [],
            excluded_custom_audiences: [],
            instream_video_skippable_excluded: false,
            app_store: 'All',
            user_os: [],
            user_device: [],
            excluded_user_device: [],
            wireless_carrier: [],
        },
        enableReinitialize: false,
        validationSchema: salesCampaignFormSchema,
        onSubmit: (values, { setSubmitting, resetForm }) => {
            const { budget, name, bid_strategy, app_store, destination_type, optimization_goal, attribution_spec, billing_event, daily_budget, bid_amount, roas_average_floor, lifetime_budget, geo_locations, excluded_geo_locations, locales, promoted_object, custom_audiences, excluded_custom_audiences, ageOriginal, genders, ageRange, is_budget_schedule_enabled, budget_schedule_specs, bid_adjustments, instream_video_skippable_excluded, user_os, user_device, excluded_user_device, wireless_carrier, is_incremental_attribution_enabled, facebookPages, whatsAppNumbers } = values;

            // Filter attribution_spec based on optimization_goal and window_days
            const filteredAttributionSpec = attribution_spec.filter(spec => {
                // Exclude ENGAGED_VIDEO_VIEW if optimization_goal is 'VALUE'
                if (optimization_goal === 'VALUE' && spec.event_type === 'ENGAGED_VIDEO_VIEW') {
                    return false;
                }
                // Exclude items where window_days is null
                return spec.window_days !== null;
            });

            if (["OFFSITE_CONVERSIONS", "VALUE"].includes(values.optimization_goal) && destination_type !== 'APP' && values.promoted_object?.length == 0) {
                setSubmitting(false)
                return;
            }

            if ((values.optimization_goal === "QUALITY_CALL") && values.facebookPages?.length === 0) {
                setSubmitting(false)
                return;
            }

            const bidDetails = (bid_strategy === 'LOWEST_COST_WITH_BID_CAP' || bid_strategy === 'COST_CAP')
                ? { bid_amount: bid_amount * 100 }
                : bid_strategy === 'LOWEST_COST_WITH_MIN_ROAS'
                    ? { roas_average_floor: roas_average_floor * 100 }
                    : {};


            const geoLocations = geo_locations?.length ? transformGeolocations(geo_locations) : null;
            const excludedGeoLocations = excluded_geo_locations?.length ? transformExcludeGeolocations(excluded_geo_locations) : null;

            const genderList = genders?.length > 0 ? { genders: genders } : null;
            const detailedTargetList = interests?.length > 0 ? transformDetailedTargets(interests) : null;
            const filteredPlacements = Object.fromEntries(
                Object.entries(placements).filter(([_, value]) => value.length > 0)
            );

            const localeList = locales?.length > 0 ? { locales: locales } : null;

            const updateStartTime = (startDate, time) => {
                return moment(startDate)
                    .set({
                        hours: parseInt(time.split(":")[0], 10),
                        minutes: parseInt(time.split(":")[1], 10),
                        seconds: 0,
                    })
                    .unix(); // Useing unix() to get Unix timestamp
            };

            const updateEndTime = (endDate, time) => {
                return moment(endDate)
                    .set({
                        hours: parseInt(time.split(":")[0], 10),
                        minutes: parseInt(time.split(":")[1], 10),
                        seconds: 0,
                    })
                    .unix();
            };

            const updatedStartTime = updateStartTime(startDate, startTime);
            const updatedEndTime = updateEndTime(endDate, endTime);

            let transformedPromotedObject;

                if (
                    (optimization_goal === 'OFFSITE_CONVERSIONS' || optimization_goal === 'VALUE') 
                    && !['APP', 'WHATSAPP'].includes(destination_type)
                ) {
                transformedPromotedObject = {
                    pixel_id: promoted_object[0].pixel_id,
                    custom_event_type: promoted_object[0].custom_event_type,
                };

                if (promoted_object[0].custom_conversion_id) {
                    transformedPromotedObject.custom_conversion_id = promoted_object[0].custom_conversion_id;
                }

                if (promoted_object[0].pixel_rule) {
                    transformedPromotedObject.pixel_rule = promoted_object[0].pixel_rule;
                }

                if (promoted_object[0].custom_event_str) {
                    transformedPromotedObject.custom_event_str = promoted_object[0].custom_event_str;
                }
            } else if (optimization_goal === 'QUALITY_CALL') {
                transformedPromotedObject = {
                    page_id: facebookPages[0].page_id,
                };
            } else if (destination_type === 'APP') {
                transformedPromotedObject = promoted_object[0]
            } else if (destination_type === 'WHATSAPP') {
                const normalizeToObject = (input) => {
                    if (Array.isArray(input)) {
                    return input.length > 0 ? input[0] : {};
                    }
                    return input || {};
                };
                const rawPromoted = normalizeToObject(promoted_object);
                const { page_id, whatsapp_phone_number,  pixelName, adsAccountId, ...promotedObj } = rawPromoted;

                const whatsAppObj = normalizeToObject(whatsAppNumbers);
                const facebookPageObj = normalizeToObject(facebookPages);
            
                // Merge all objects
                const mergedRaw = {
                ...promotedObj,
                ...whatsAppObj,
                ...facebookPageObj,
                };

                // Remove duplicate values (same value with different keys allowed)
                const seenValues = new Set();
                const mergedUnique = {};
            
                for (const [key, value] of Object.entries(mergedRaw)) {
                const valKey = typeof value === 'string' ? value.trim() : value;
                if (!seenValues.has(valKey) && key !== 'adsAccountId') {
                    seenValues.add(valKey);
                    mergedUnique[key] = value;
                    }
                }

                transformedPromotedObject = mergedUnique;
            }

            const campaign = {
                name: campaignName,
                objective,
                campaign_id,
                ...(hasBidStrategy && {
                    bid_strategy: bidStrategy,
                    ...(lifetime_budget && { lifetime_budget: lifetimeBudget }),
                    ...(dailyBudget && { daily_budget: dailyBudget }),
                    ...(pacing_type && { pacing_type: ["day_parting"] })
                })
            };

            const payload = {
                name,
                destination_type, optimization_goal, billing_event,
                adsAccount: {
                    adsAccountId: adsAccountData?.account_id,
                    adsAccountName: adsAccountData?.name
                },
                campaign,
                targeting: {
                    ...filteredPlacements,
                    custom_audiences: custom_audiences,
                    excluded_custom_audiences: excluded_custom_audiences,
                    age_min: ageOriginal.age_min,
                    age_max: showAdvantage ? 65 : ageOriginal.age_max,
                    ...(
                        showAdvantage
                          ? {
                              targeting_automation: {
                                advantage_audience: 1
                              },
                              age_range: [ageRange?.age_min, ageRange?.age_max]
                            }
                          : {
                              targeting_automation: {
                                advantage_audience: 0
                              }
                            }
                    ),
                    ...geoLocations,
                    ...excludedGeoLocations,
                    ...genderList,
                    ...detailedTargetList,
                    ...localeList,
                    instream_video_skippable_excluded: instream_video_skippable_excluded,
                    ...(app_store !== 'All' && {
                        user_device: user_device,
                        excluded_user_device: excluded_user_device,
                        user_os: user_os,
                    }),
                    wireless_carrier: wireless_carrier,
                },
                promoted_object: transformedPromotedObject,
                ...bidDetails,
                ...(!hasBidStrategy && {
                    bid_strategy,
                    ...(budget === 'daily_budget' ? { daily_budget: Number(daily_budget) * 100 } : {}),
                    ...(budget === 'lifetime_budget' ? { lifetime_budget: Number(lifetime_budget) * 100 } : {}),
                    ...(adSchedule && adSchedule.length > 0 ? { pacing_type: ["day_parting"] } : {}),
                    ...(is_budget_schedule_enabled && {
                        is_budget_schedule_enabled,
                        budget_schedule_specs: budget_schedule_specs.map(spec => ({
                            ...spec,
                            budget_value: spec.budget_value_type === 'ABSOLUTE'
                            ? Math.round(spec.budget_value * 100)
                            : spec.budget_value
                        }))
                    })
                }),
                currency: currencyCode,
                start_time: updatedStartTime,
                ...(budget === 'lifetime_budget' ? { end_time: updatedEndTime } : {}),
                ...(budget === 'daily_budget' && showEndDate ? { end_time: updatedEndTime } : {}),
                ...(bid_adjustments && { bid_adjustments: bid_adjustments }), 
                adset_schedule: adSchedule,
                ...(['OFFSITE_CONVERSIONS', 'VALUE'].includes(optimization_goal) && destination_type !== 'APP' && {
                    is_incremental_attribution_enabled: is_incremental_attribution_enabled,
                    attribution_spec: filteredAttributionSpec.length > 0 ? filteredAttributionSpec : undefined
                }),
            };

            const filteredPayload = removeEmptyValues(payload);

            // console.log({ filteredPayload });


            createAdset({ payload: filteredPayload, adsAccountId: firstAdAccountId })
                .unwrap()
                .then(response => {
                    toast({
                        position: "top-right",
                        title: "Ad set created",
                        status: "success",
                        duration: 4000,
                        isClosable: true,
                    });
                    resetForm();
                    onSaveSuccess();
                    onClose();
                })
                .catch(error => {
                    let errorMessage = "An error occurred while creating the adset.";
                    if (error && error.data && error.data.message) {
                        errorMessage = error.data.message.replace(/"/g, '');
                    } else if (error.message) {
                        errorMessage = error.message.replace(/"/g, '');
                    }
                    toast({
                        position: "top-right",
                        title: "Error updating Adset",
                        description: errorMessage,
                        status: "error",
                        duration: 4000,
                        isClosable: true,
                    });
                })
                .finally(() => {
                    setSubmitting(false);
                });
        }
    },
    );


    const {
        name,
        destination_type,
        optimization_goal,
        promoted_object,
        budget,
        targeting,
        bid_strategy,
    } = data;

    const optimizationOptions = optimization_goal[adSetFormik.values.destination_type] || [];
    const promotedObjects = promoted_object?.[adSetFormik.values.destination_type] || {};
    const promotedObjectFields = promotedObjects?.[adSetFormik.values.optimization_goal] || {};

    useEffect(() => {
        if( adSetFormik.values.bid_strategy === 'COST_CAP') {
            adSetFormik.setFieldValue('billing_event', 'IMPRESSIONS');
        }
    }, [adSetFormik.values.bid_strategy]);

    useEffect(() => {
        if(['WEBSITE_AND_PHONE_CALL','WHATSAPP'].includes(adSetFormik?.values?.destination_type)) {
            adSetFormik?.setFieldValue('attribution_spec', [
                { event_type: 'CLICK_THROUGH', window_days: 7 },
                { event_type: 'VIEW_THROUGH', window_days: 1 },
                { event_type: 'ENGAGED_VIDEO_VIEW', window_days: null }
            ]);
        }
    }, [adSetFormik?.values?.destination_type]);

    useEffect(() => {
        if (adSetFormik?.values?.destination_type === 'APP') {
            adSetFormik?.setFieldValue('app_store', 'google_play');
        }
    }, [adSetFormik?.values?.destination_type]);

    useEffect(() => {
        if (adSetFormik.values.destination_type == "PHONE_CALL") {
            adSetFormik.setFieldValue('optimization_goal', 'QUALITY_CALL');
            adSetFormik.setFieldValue('bid_strategy', 'LOWEST_COST_WITHOUT_CAP');
        }
        if (adSetFormik.values.destination_type === "WEBSITE") {
            // Update the optimization goal only if it's not already set
            if (adSetFormik.values.optimization_goal === "QUALITY_CALL") {
                adSetFormik.setFieldValue('optimization_goal', 'OFFSITE_CONVERSIONS');
            }
        }
        if (adSetFormik.values.destination_type === "WHATSAPP") {
            // Update the optimization goal only if it's not already set
            if (!hasBidStrategy) {
                adSetFormik.setFieldValue('optimization_goal', 'OFFSITE_CONVERSIONS');
            }
        }
    }, [adSetFormik.values.destination_type]);

    useEffect(() => {
        if (adSetFormik.values.budget === 'lifetime_budget') {
            const newEndDate = new Date(startDate);
            newEndDate.setDate(newEndDate.getDate() + 30); // 30 days from startDate
            setEndDate(newEndDate);
        }
    }, [adSetFormik.values.budget]);

    useEffect(() => {
        if (endDate <= startDate) {
            setErrorDateMessage('End date must be greater than start date.');
        } else {
            setErrorDateMessage('');
        }
    }, [startDate, endDate]);

    useEffect(() => {
        if (adSchedule.length > 0 && adSetFormik.values.budget === 'lifetime_budget') {
            // Ensure that each schedule entry contains the correct timezone
            const updatedAdSchedule = adSchedule.map(scheduleEntry => ({
                ...scheduleEntry,
                timezone_type: timezone
            }));
            setAdSchedule(updatedAdSchedule);
        } else if (adSetFormik.values.budget === 'daily_budget') {
            setAdSchedule([]);
        }
    }, [timezone, adSetFormik.values.budget]);

    const handleAdScheduleChange = (newSchedule) => {
        const adScheduleData = newSchedule.map(scheduleEntry => ({
            ...scheduleEntry,
            timezone_type: timezone
        }));
        setAdSchedule(adScheduleData);
    };

    const handleBudgetChange = (e) => {
        const selectedBudgetType = e.target.value;
        setBudgetType(selectedBudgetType);

        // Adjust budget values based on selection
        if (selectedBudgetType === 'daily_budget') {
            adSetFormik.setFieldValue('lifetime_budget', Number(adSetFormik.values.lifetime_budget));
        } else if (selectedBudgetType === 'lifetime_budget') {
            adSetFormik.setFieldValue('daily_budget', Number(adSetFormik.values.daily_budget));
        }
        adSetFormik.setFieldValue('budget', selectedBudgetType);
    };

    const handleLocationsChange = (locations) => {

        // Separate included and excluded locations
        const includedLocations = locations.filter(location => location.inclusionType === 'include');
        const excludedLocations = locations.filter(location => location.inclusionType === 'exclude');

        // Update the formik fields based on inclusion type
        adSetFormik.setFieldValue('geo_locations', includedLocations);
        adSetFormik.setFieldValue('excluded_geo_locations', excludedLocations);

        setTimeout(() => {
            adSetFormik.validateField('geo_locations');
        }, 0);
    };

    const handleAgeGenderChange = (updatedAgeGender) => {
        const { age_min, age_max, gender } = updatedAgeGender;
        const genderArray = genderMap[gender] || [];

        // Update the Formik state for ageOriginal and gender
        adSetFormik.setFieldValue('ageOriginal', { age_min, age_max });
        adSetFormik.setFieldValue('genders', genderArray);
    };

    const handleAgeRangeChange = (data) => {
        const { age_min, age_max } = data;
        adSetFormik.setFieldValue('ageRange', { age_min, age_max });
    }

    const handleInterestsChange = (interests) => {
        setInterests(interests);
    };

    const handleCustomAudienceChange = (id) => {
        // Get the current excluded custom audiences from Formik field value
        const custom_audiences = [...adSetFormik?.values?.custom_audiences];

        // Check if the id already exists in the array based on the `id` field
        const isIdAlreadyExcluded = custom_audiences.some(audience => audience.id === id);

        if (!isIdAlreadyExcluded) {
            // If not, append the new id to the array (creating an object with the id)
            custom_audiences.push({ id });
        }

        adSetFormik.setFieldValue('custom_audiences', custom_audiences);
    };


    const handleIncludeCustomAudienceChange = (audiences) => {
    
        const cleanedAudiences = audiences.map(audience => ({
            id: audience?.audience_id,
            name: audience?.name,
        }));
    
        // Just replace the form value with current list
        adSetFormik.setFieldValue('custom_audiences', cleanedAudiences);
    };    


    const handleExcludeCustomAudienceChange = (audiences) => {
        const excluded_custom_audiences = audiences.map(audience => ({
            id: audience?.audience_id,
            name: audience?.name,
        }));

        // Get the current excluded
        const currentExcluded = [...adSetFormik?.values?.excluded_custom_audiences];

        // Split current audiences into those that have only 'id'
        const onlyIdAudiences = currentExcluded.filter(audience => audience?.id && !audience?.name);

        // If there are audiences with both 'id' and 'name', replace them, but keep the ones with only 'id' unchanged
        const uniqueExcluded = [
            ...onlyIdAudiences, // Keep only 'id' audiences unchanged
            ...excluded_custom_audiences, // Only add the new audiences that have both 'id' and 'name'
        ].filter((value, index, self) =>
            index === self.findIndex((v) => v.id === value.id)
        );

        adSetFormik.setFieldValue('excluded_custom_audiences', uniqueExcluded);
    };

    const handlePixelIdSelection = (option, adsAccountId) => {
        const updatedPromotedObjects = adSetFormik.values.promoted_object.filter(
            obj => obj.adsAccountId !== adsAccountId
        );

        updatedPromotedObjects.push({
            adsAccountId,
            pixel_id: option?.id,
            pixelName: option?.name
        });

        adSetFormik.setFieldValue('promoted_object', updatedPromotedObjects);
    };

    const handleEventTypeSelection = (option, adsAccountId) => {

        const updatedPromotedObjects = adSetFormik.values.promoted_object.map(obj =>
            obj.adsAccountId === adsAccountId ? {
                ...option,
                adsAccountId,
                pixel_id: obj?.pixel_id,
                pixelName: obj?.pixelName
            } : obj
        );

        // If the adsAccountId isn't found, add a new promoted object
        if (!adSetFormik.values.promoted_object.find(obj => obj.adsAccountId === adsAccountId)) {
            updatedPromotedObjects.push({
                adsAccountId,
                ...option
            });
        }

        adSetFormik.setFieldValue('promoted_object', updatedPromotedObjects);
    };

    const handleFacebookPageSelection = (pageId, adsAccountId) => {
        const { facebookPages } = adSetFormik.values;

        const updatedAccounts = facebookPages?.map(obj =>
            obj.adsAccountId === adsAccountId ? { ...obj, page_id: pageId } : obj
        );

        if (!facebookPages?.find(obj => obj.adsAccountId === adsAccountId)) {
            updatedAccounts.push({ adsAccountId, page_id: pageId });
        }

        adSetFormik.setFieldValue('facebookPages', updatedAccounts);
    };

    const handleWhatsAppSelection = (number, adsAccountId) => {
          const prev = whatsAppNumbersRef.current || [];
      
          const existingIndex = prev.findIndex(obj => obj.adsAccountId === adsAccountId);
      
          let updated;
          if (existingIndex !== -1) {
            updated = [...prev];
            updated[existingIndex] = {
              ...updated[existingIndex],
              whatsapp_phone_number: number,
            };
          } else {
            updated = [...prev, { adsAccountId, whatsapp_phone_number: number }];
          }
      
          whatsAppNumbersRef.current = updated;
          adSetFormik.setFieldValue('whatsAppNumbers', updated);
    };

    const handleAppChange = (app) => {
        if (app && app.length > 0) {
            const prev = Array.isArray(adSetFormik.values.promoted_object)
            ? adSetFormik.values.promoted_object[0]
            : {};

            const updatedPromotedObject = [{
            ...prev,
            application_id: app[0].id,
            object_store_url: app[0].original_object_url
            }];

            adSetFormik.setFieldValue('promoted_object', updatedPromotedObject);
        } else {
            adSetFormik.setFieldValue('promoted_object', []);
        }
    };

    const handleAppEventChange = (appEvent) => {
    if (appEvent && Object.keys(appEvent).length > 0) {
        const prev = Array.isArray(adSetFormik.values.promoted_object)
        ? adSetFormik.values.promoted_object[0]
        : {};

        const updatedPromotedObject = [{
        ...prev,
        custom_event_type: appEvent.value
        }];

        adSetFormik.setFieldValue('promoted_object', updatedPromotedObject);
    } else {
        const prev = Array.isArray(adSetFormik.values.promoted_object)
        ? { ...adSetFormik.values.promoted_object[0] }
        : {};

        delete prev.custom_event_type;

        adSetFormik.setFieldValue('promoted_object', [prev]);
    }
    };
    
    const handleIncludeDeviceChange = (devices) => {

        const user_devices = devices.map(device => device?.name);
  
        adSetFormik.setFieldValue('user_device', user_devices);
    };
  
    const handleExcludeDeviceChange = (devices) => {

        const excluded_user_device = devices.map(device => device?.name);

        adSetFormik.setFieldValue('excluded_user_device', excluded_user_device);
    };

    const handleOsVersionChange = (min, max, AppStore) => {
        if (min && max) {
            let formattedVersion;

            // Check if AppStore is 'itunes' and switch to iOS version format
            if (AppStore === 'itunes') {
                if (max === 'None') {
                    formattedVersion = `iOS_ver_${min}_and_above`;
                } else {
                    formattedVersion = `iOS_ver_${min}_to_${max}`;
                }
            } else if (AppStore === 'google_play') {
                // If AppStore is 'google_play', use the Android version format
                if (max === 'None') {
                    formattedVersion = `Android_ver_${min}_and_above`;
                } else {
                    formattedVersion = `Android_ver_${min}_to_${max}`;
                }
            }

            adSetFormik.setFieldValue('user_os', [formattedVersion]);
        } else {
            // If either min or max is missing, set user_os to an empty array
            adSetFormik.setFieldValue('user_os', []);
        }
    };

    const handleBidChange = useCallback((data) => {
        adSetFormik.setFieldValue('bid_adjustments', data);
    }, [adSetFormik]);

    const handleSave = () => {
        // Set all fields to touched to trigger validation
        Object.keys(adSetFormik.values).forEach(field => {
            adSetFormik.setFieldTouched(field, true);
        });

        setIsSaving(true);

        const errors = adSetFormik.errors;
        const errorKeys = Object.keys(errors);

        // Check for validation errors
        if (errorKeys.length > 0) {
            const firstErrorKey = errorKeys[0];

            // Check if promoted_object is empty or has invalid entries
            const promotedObject = adSetFormik.values.promoted_object;
            const optimizationGoal = adSetFormik.values.optimization_goal;
            const destinationType = adSetFormik.values.destination_type;

            if (
                (optimizationGoal === 'OFFSITE_CONVERSIONS' || optimizationGoal === 'VALUE') && destinationType !== 'APP' &&
                (
                    promotedObject.length === 0 ||
                    promotedObject.some(obj => !obj.custom_event_type || !obj.pixel_id)
                )
            ) {
                // Scroll to the error container
                const errorContainer = document.querySelector('.error-container');
                if (errorContainer) {
                    errorContainer.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
                return;
            }

            // First, try to scroll to the element by name
            const errorElementByName = document.querySelector(`[name="${firstErrorKey}"]`);
            if (errorElementByName) {
                errorElementByName.scrollIntoView({ behavior: 'smooth', block: 'center' });
                return;
            }

            // If not found, try to scroll to the element by error class
            const errorElementByClass = document.querySelector(`.error-class-${firstErrorKey}`);
            if (errorElementByClass) {
                errorElementByClass.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            return;
        }

        const promotedObject = adSetFormik.values.promoted_object;
        const destinationType = adSetFormik.values.destination_type;

        if (
            (destinationType === 'APP') &&
            (
                // Check if promotedObject is an array and is invalid
                (Array.isArray(promotedObject) && (
                    promotedObject.length === 0 ||
                    promotedObject.some(obj =>
                        !obj ||
                        !obj.application_id ||
                        !obj.object_store_url ||
                        !obj.custom_event_type 
                    )
                ))
            )
        ) {
            const errorContainer = document.querySelector('.error-container');
            if (errorContainer) {
                errorContainer.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            return;
        }

        if (pacing_type && pacing_type.includes('day_parting') && !(adSchedule.length > 0)) {
            const errorContainer = document.querySelector('.error-class-schedule-time');
            if (errorContainer) {
                errorContainer.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            return;
        }

        if (
            (adSetFormik.values.budget === 'lifetime_budget' && (errorDateMessage || endDate <= startDate)) ||
            (adSetFormik.values.budget === 'daily_budget' && showEndDate && (errorDateMessage || endDate <= startDate))
        ) {
            return;
        }

        adSetFormik.handleSubmit();
    };

    const buildTargetingSpec = useMemo(() => {
        const geoLocations = adSetFormik.values.geo_locations || [];
        const locales = adSetFormik.values.locales || [];
        const genders = adSetFormik.values.genders || [];
        const age_min = adSetFormik.values.ageOriginal.age_min;
        const age_max = adSetFormik.values.ageOriginal.age_max;
        const custom_audiences = adSetFormik.values.custom_audiences;
        const excluded_custom_audiences = adSetFormik.values.excluded_custom_audiences;
        const user_os = adSetFormik?.values?.user_os;

        const geoLocationList = geoLocations?.length ? transformGeolocations(geoLocations) : null;
        const genderList = genders?.length > 0 ? { genders: genders } : null;
        const detailedTargetList = interests?.length > 0 ? transformDetailedTargets(interests) : null;
        const localeList = locales?.length > 0 ? { locales: locales } : null;

        const filteredPlacements = Object.fromEntries(
            Object.entries(placements).filter(([_, value]) => value.length > 0)
        );

        return {
            ...geoLocationList,
            age_min,
            age_max,
            ...genderList,
            ...detailedTargetList,
            ...localeList,
            ...filteredPlacements,
            custom_audiences: custom_audiences,
            excluded_custom_audiences: excluded_custom_audiences,
            user_os
        };
    }, [
        adSetFormik.values.geo_locations,
        adSetFormik.values.locales,
        adSetFormik.values.ageOriginal,
        adSetFormik.values.genders,
        adSetFormik.values.custom_audiences,
        adSetFormik.values.excluded_custom_audiences,
        adSetFormik.values?.user_os,
        interests,
        placements,
    ]);

    return (
        <Box borderRadius="md" width="100%">
            <Flex gap={'15px'} direction={{ base: "column", md: "row" }}>
                <Flex flex={0.6} direction="column" overflowY="auto" maxHeight="calc(100vh - 200px)">
                    <Box width="100%">
                        <form onSubmit={adSetFormik.handleSubmit}>
                            {/* Ad Set Name */}
                            <Flex direction="column" mb={4} bg={backgroundColor} p={4} borderRadius="md" gap={1}>
                                <Flex>
                                    <Text fontWeight="bold">{name.label}</Text>
                                    <Text display="inline-flex" fontSize="18px" color="red.400">*</Text>
                                </Flex>
                                <Input
                                    name="name"
                                    placeholder={`Enter ${name.label}`}
                                    bg="#fff"
                                    value={adSetFormik.values.name}
                                    onChange={adSetFormik.handleChange}
                                    onBlur={adSetFormik.handleBlur}
                                    borderRadius="md"
                                    size={'sm'}
                                />
                                {adSetFormik.touched.name && adSetFormik.errors.name && (
                                    <Text fontSize={'sm'} color="red.500">{adSetFormik.errors.name}</Text>
                                )}
                            </Flex>


                            {/* Conversion Location */}
                            <Flex direction="column" mb={4} bg={backgroundColor} p={4} borderRadius="md" gap={2}>
                                <Text fontWeight="bold">{destination_type.label1}</Text>
                                <Text fontWeight="bold">{destination_type.label2}</Text>
                                <RadioGroup
                                    name="destination_type"
                                    onChange={(value) => {
                                        adSetFormik.setFieldValue('destination_type', value)
                                        adSetFormik.setFieldValue('promoted_object', [])
                                    }}
                                    value={adSetFormik.values.destination_type}
                                >
                                    <Stack spacing={4} direction='column' mb={2}>
                                        {destination_type.options?.map((option) => {
                                            const isPhoneCall = option.value === "PHONE_CALL";
                                            const isDisabled = isPhoneCall && bidStrategy && bidStrategy !== "LOWEST_COST_WITHOUT_CAP";
                                            const tooltipLabel = "This option isn't available with the bid strategy you've selected.";

                                            return (
                                                <Flex key={option.value} align="center">
                                                    {isDisabled ? (
                                                        <Tooltip label={tooltipLabel} hasArrow placement="top" fontSize={'12px'} p={2}>
                                                            <Box cursor="not-allowed">
                                                                <Radio
                                                                    colorScheme='brand'
                                                                    value={option.value}
                                                                    isDisabled={true}
                                                                />
                                                            </Box>
                                                        </Tooltip>
                                                    ) : (
                                                        <Radio
                                                            colorScheme='brand'
                                                            value={option.value}
                                                            isDisabled={false}
                                                        />
                                                    )}

                                                    <Stack ml={2} spacing={0}>
                                                        <Text>{option.label1}</Text>
                                                        <Text fontSize='sm'>{option.label2}</Text>
                                                    </Stack>
                                                </Flex>
                                            );
                                        })}
                                    </Stack>
                                </RadioGroup>
                                {adSetFormik.touched.destination_type && adSetFormik.errors.destination_type && (
                                    <Text fontSize={'sm'} color="red.500">{adSetFormik.errors.destination_type}</Text>
                                )}

                                {adSetFormik.values.destination_type && (
                                    <Flex direction="column" gap={2} mb={2}>
                                        <Flex alignItems={'center'}>
                                            <Text fontWeight="bold">{optimization_goal.label}</Text>
                                            <Tooltip
                                                borderRadius={5}
                                                label={
                                                    <Box fontSize="sm" p={'5px'}>
                                                        <Text>Choose what you want people to do when they see your ads. Your campaign objective should align with your overall business goals.</Text>
                                                    </Box>
                                                }
                                                fontSize="md"
                                            >
                                                <InfoOutlineIcon pointerEvents="all" ml={2} />
                                            </Tooltip>
                                        </Flex>

                                        {optimizationOptions.length === 1 ? (
                                            <Tooltip
                                                borderRadius={5}
                                                label={
                                                <Box fontSize="sm" p="5px">
                                                    <Text>
                                                        This is the only performance goal available for this conversion location.
                                                    </Text>
                                                </Box>
                                                }
                                                fontSize="md"
                                                >
                                                <Text
                                                    border="1px"
                                                    borderColor={borderColor}
                                                    _hover={{ borderColor: hoverBorderColor }}
                                                    bg={textBackground}
                                                    color={inputTextColor}
                                                    borderRadius="md"
                                                    p={2}
                                                    fontSize="14px"
                                                    height="32px"
                                                    display="flex"
                                                    alignItems="center"
                                                >
                                                    {optimizationOptions[0].label}
                                                </Text>
                                            </Tooltip>
                                        ) : (
                                            <Select
                                                name="optimization_goal"
                                                value={adSetFormik.values.optimization_goal}
                                                onChange={(e) => adSetFormik.setFieldValue('optimization_goal', e.target.value)}
                                                onBlur={adSetFormik.handleBlur}
                                                bg="#fff"
                                                borderRadius="md"
                                                size={'sm'}
                                                isDisabled={bidStrategy === 'LOWEST_COST_WITH_MIN_ROAS'}
                                            >
                                                {optimizationOptions.map((option, index) => (
                                                    <option key={`${option.value}-${index}`} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}

                                            </Select>
                                        )}
                                        {adSetFormik.touched.optimization_goal && adSetFormik.errors.optimization_goal && (
                                            <Text fontSize={'sm'} color="red.500">{adSetFormik.errors.optimization_goal}</Text>
                                        )}
                                    </Flex>
                                )}
                                
                                {adSetFormik.values.destination_type === 'APP' ? 
                                    <Flex direction="column" gap={2} className='error-container'>
                                        <Flex direction="column" gap={1}>
                                            <Text fontWeight="bold">{promotedObjectFields?.app_store?.label}</Text>
                                            <Select
                                                name="app_store"
                                                value={adSetFormik.values.app_store}
                                                onChange={(e) => adSetFormik.setFieldValue('app_store', e.target.value)}
                                                onBlur={adSetFormik.handleBlur}
                                                bg="#fff"
                                                borderRadius="md"
                                                size={'sm'}
                                            >
                                                {promotedObjectFields?.app_store?.options?.map((option, index) => (
                                                    <option key={`${option.value}-${index}`} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </Select>
                                        </Flex>
                                        <AppStore
                                            adsAccountId={adsAccount.adsAccountId}
                                            adSetFormik={adSetFormik?.values}
                                            preFilledData={''}
                                            onAppChange={(app) => handleAppChange(app)}
                                            isSaving={isSaving}
                                        />
                                        <Flex direction={'column'} gap={1}>
                                            <AppEvents 
                                              adsAccountId={firstAdAccountId}
                                              adSetFormik={{
                                                promoted_object: {
                                                    application_id: adSetFormik.values?.promoted_object[0]?.application_id
                                                }
                                              }}
                                              onIncludeAppEventChange={handleAppEventChange}
                                            />
                                            {adSetFormik?.values?.promoted_object && !adSetFormik?.values?.promoted_object[0]?.custom_event_type && isSaving && (
                                                <Text fontSize="sm" color="red.500" mt={-2}>
                                                    To use the Sales objective, please choose an app event.
                                                </Text>
                                            )}
                                        </Flex>
                                    </Flex>
                                :null}

                                <Flex direction="column" className='error-container'>
                                    {/* Promoted Object */}
                                    {(adSetFormik.values.optimization_goal === 'OFFSITE_CONVERSIONS' || adSetFormik.values.optimization_goal === 'VALUE') && adSetFormik.values.destination_type !== 'APP' && (
                                        <>
                                            <PixelConversionSelector
                                                adsAccount={{
                                                    adsAccountId: adsAccountData?.account_id,
                                                    adsAccountName: adsAccountData?.name,
                                                }}
                                                adSetFormik={adSetFormik}
                                                promotedObjectFields={promotedObjectFields}
                                                promotedObjectData={''}
                                                onSelectedPixelId={(option) => handlePixelIdSelection(option, adsAccount.adsAccountId)}
                                                // onSelectedEventType={(type) => handleEventTypeSelection(type, account.adsAccountId)}
                                                onSelectedEventType={(option, adsAccountId) => handleEventTypeSelection(option, adsAccountId)}
                                                onBlur={adSetFormik.handleBlur}
                                                isSaving={isSaving}
                                                objective={objective}
                                                context={'single'}
                                            />
                                            {/* <Box mt={-2}>
                                                <BotNullifierCampaigns
                                                    adsAccount={adsAccountData}
                                                    promoted_object={adSetFormik?.values?.promoted_object}
                                                    botNullifyData={''}
                                                    onCustomAudienceChange={(id) => handleCustomAudienceChange(id)}
                                                />
                                            </Box> */}
                                        </>
                                    )}

                                    {adSetFormik.touched.promoted_object && adSetFormik.errors.promoted_object === 'Pixel id is required' && (
                                        <Text fontSize={'sm'} color="red.500" className="error-class-promoted_object">
                                            {/* {'Pixels & events are required'} */}
                                        </Text>
                                    )}

                                    <Flex gap={2} direction="column">
                                        {(adSetFormik.values.optimization_goal === 'QUALITY_CALL' || (['OFFSITE_CONVERSIONS', 'CONVERSATIONS', 'LINK_CLICKS', 'REACH', 'IMPRESSIONS'].includes(adSetFormik.values.optimization_goal) && adSetFormik.values.destination_type === 'WHATSAPP')) && (
                                            <>
                                                <Flex alignItems="center">
                                                    <Text fontWeight="bold">Facebook pages</Text>
                                                    <Text display="inline-flex" fontSize="18px" color="red.400" mr={2}>*</Text>
                                                    <Tooltip
                                                        borderRadius={5}
                                                        label={
                                                            <Box fontSize="sm" p="5px">
                                                                <Text>Choose the Facebook Page that you want to promote. If the Page you want isn't listed, contact the Page Admin.</Text>
                                                            </Box>
                                                        }
                                                        fontSize="md"
                                                    >
                                                        <InfoOutlineIcon pointerEvents="all" />
                                                    </Tooltip>
                                                </Flex>

                                                <FaceBookPageAdspressoSelector
                                                    adsAccount={{
                                                        adsAccountId: adsAccountData?.account_id,
                                                        adsAccountName: adsAccountData?.name,
                                                    }}
                                                    selectedPageId={''}
                                                    onSelectedPage={(page) => handleFacebookPageSelection(page, adsAccount.adsAccountId)}
                                                    context={'single'}
                                                />

                                                {adSetFormik.touched.facebookPages && adSetFormik.errors.facebookPages && (
                                                    <Text fontSize={'sm'} color="red.500">{adSetFormik.errors.facebookPages}</Text>
                                                )}
                                            </>
                                        )}
                                    </Flex>

                                    {/* WhatsApp */}
                                    {(adSetFormik.values.destination_type === 'WHATSAPP') ? 
                                        <Flex direction={'column'} gap={2} my={2}>
                                            <Flex alignItems="center" gap={2}>
                                                    <Text fontWeight="bold">Message destinations</Text>
                                                    <Tooltip
                                                        borderRadius={5}
                                                        label={
                                                            <Box fontSize="sm" p="5px">
                                                                <Text>These apps are where you’ll chat with people that respond to your ad. Your budget will be shared across these apps.</Text>
                                                            </Box>
                                                        }
                                                        fontSize="md"
                                                    >
                                                        <InfoOutlineIcon pointerEvents="all" />
                                                    </Tooltip>
                                            </Flex>
                                            <Text fontWeight={'bold'}>WhatsApp</Text>
                                            <WhatsAppSelector
                                                adsAccount={{
                                                    adsAccountId: adsAccountData?.account_id,
                                                    adsAccountName: adsAccountData?.name,
                                                    businessId: adsAccountData?.business?.id
                                                }}
                                                selectedWhatsAppNumber={''}
                                                onSelectedNumber={(number) => handleWhatsAppSelection(number, adsAccount.adsAccountId)}
                                            />
                                        </Flex>
                                    :null}

                                </Flex>

                                {/* Bid Amount Field */}
                                <Flex direction="column">
                                    {hasBidStrategy ? 
                                    <>
                                        {adSetFormik.values.optimization_goal &&
                                            bid_strategy?.option[1]?.optimization_goal.includes(adSetFormik.values.optimization_goal) && (
                                                <Flex direction="column">
                                                    <Flex alignItems={'center'}>
                                                        <Text mb={2} fontWeight="bold">
                                                            {bidStrategy === 'LOWEST_COST_WITH_BID_CAP' ? 'Bid control' : 'Cost per result goal'}{" "}
                                                            {bidStrategy ? null : <Text as="span" fontWeight="normal">Optional</Text>}
                                                        </Text>
                                                        <Tooltip
                                                            borderRadius={5}
                                                            label={
                                                                <Box fontSize="12px" p={'5px'}>
                                                                    <Text>
                                                                        {bidStrategy === 'LOWEST_COST_WITH_BID_CAP' ?
                                                                            `Set a bid control if you want to control your maximum bid in every auction. By setting a bid control, you're telling Meta how to bid on results. If your ad set requires a conversion window, use that to determine your bid control too. If you don't set one, Meta will focus on spending your entire budget and getting the most results.
                                                                        Bid controls are only available with some performance goal selections.`
                                                                            :
                                                                            `Set a goal if you're aiming for a certain cost per result. To set your goal,
                                                                        use cost per result data from previous campaigns with the same optimisation event
                                                                        and attribution settings. If you don't set a cost per result goal, Meta will focus
                                                                        on spending your entire budget and getting the most results.<br></br>
                                                                        Cost per result goal is only available with some performance goal selections.`}
                                                                    </Text>
                                                                </Box>
                                                            }
                                                            fontSize="md"
                                                        >
                                                            <InfoOutlineIcon pointerEvents="all" ml={2} mb={2} />
                                                        </Tooltip>
                                                    </Flex>

                                                    <InputGroup>
                                                        <Tooltip
                                                            borderRadius={5}
                                                            p={2}
                                                            ml={20}
                                                            label="The highest-volume bid strategy doesn't have a cost per result goal."
                                                            isDisabled={bidStrategy !== 'LOWEST_COST_WITHOUT_CAP'}
                                                        >
                                                            <Input
                                                                name="bid_amount"
                                                                type="number"
                                                                placeholder={`${currencySymbol}X.XX`}
                                                                value={adSetFormik.values.bid_amount}
                                                                onChange={(e) => {
                                                                    const value = e.target.value;
                                                                    adSetFormik.handleChange(e);
                                                                    if (value) {
                                                                        adSetFormik.setFieldValue('bid_strategy', 'LOWEST_COST_WITH_BID_CAP');
                                                                        adSetFormik.setFieldValue('is_incremental_attribution_enabled', false);
                                                                    } else {
                                                                        adSetFormik.setFieldValue('bid_strategy', 'LOWEST_COST_WITHOUT_CAP');
                                                                    }
                                                                }}
                                                                onBlur={adSetFormik.handleBlur}
                                                                borderLeftRadius={'md'}
                                                                size={'sm'}
                                                                onWheel={(e) => e.target.blur()}
                                                                isDisabled={bidStrategy === 'LOWEST_COST_WITHOUT_CAP'}
                                                            />
                                                        </Tooltip>
                                                        <InputRightAddon height={'32px'} fontSize={'14px'}>
                                                            {currencyCode}
                                                        </InputRightAddon>
                                                    </InputGroup>

                                                    {adSetFormik.touched.bid_amount && adSetFormik.errors.bid_amount && (
                                                        <Text fontSize={'sm'} color="red.500">{adSetFormik.errors.bid_amount}</Text>
                                                    )}
                                                </Flex>
                                        )}
                                    </>
                                    :
                                    <>
                                        {/* Bid Amount Input Field */}
                                        {['QUALITY_CALL', 'VALUE'].includes(adSetFormik.values.optimization_goal) ? null : (() => {
                                            const goal = adSetFormik.values.optimization_goal;

                                            const bidCapOption = bid_strategy?.option?.[1];
                                            const costCapOption = bid_strategy?.option?.[2];

                                            const isBidCapMatch = bidCapOption?.optimization_goal.includes(goal);
                                            const isCostCapMatch = costCapOption?.optimization_goal.includes(goal);

                                            const currentStrategy = adSetFormik.values.bid_strategy;

                                            // Determine if we should use currentStrategy or fallback based on optimization_goal
                                            // Ignore if currentStrategy is LOWEST_COST_WITHOUT_CAP
                                            const useStrategy = currentStrategy !== 'LOWEST_COST_WITHOUT_CAP' ? currentStrategy : null;
                                            

                                            const isBidCap = useStrategy === 'LOWEST_COST_WITH_BID_CAP' || (!useStrategy && isBidCapMatch);
                                            const isCostCap = useStrategy === 'COST_CAP' || (!useStrategy && isCostCapMatch);

                                            const currentLabel = isBidCap ? 'Bid control' : isCostCap ? 'Cost per result goal' : null;

                                            const tooltipContent = isBidCap
                                            ? `Set a bid control if you want to control your maximum bid in every auction.
                                                By setting a bid control, you're telling Meta how to bid on results.
                                                If your ad set requires a conversion window, use that to determine your bid control too.
                                                If you don't set one, Meta will focus on spending your entire budget and getting the most results.
                                                Bid controls are only available with some performance goal selections.`
                                            : isCostCap
                                            ? `Optimise your campaign for certain performance goals. Don't set a cost per result goal if you want Meta to focus on spending your entire budget for the most results.`
                                            : null;

                                            const showLabelTooltip = currentLabel !== null;

                                            return (
                                            <Flex direction="column">
                                                {showLabelTooltip && (
                                                <Flex alignItems={'center'}>
                                                    <Text mb={2} fontWeight="bold">
                                                    {currentLabel} <Text as="span" fontWeight="normal">Optional</Text>
                                                    </Text>
                                                    <Tooltip
                                                    borderRadius={5}
                                                    fontSize="sm"
                                                    label={<Box whiteSpace="pre-line" p="5px" fontSize="12px">{tooltipContent}</Box>}
                                                    >
                                                    <InfoOutlineIcon pointerEvents="all" ml={2} mb={2} />
                                                    </Tooltip>
                                                </Flex>
                                                )}

                                                <InputGroup>
                                                <Tooltip
                                                    borderRadius={5}
                                                    p={2}
                                                    ml={20}
                                                    label="The highest-volume bid strategy doesn't have a cost per result goal."
                                                >
                                                    <Input
                                                    name="bid_amount"
                                                    type="number"
                                                    placeholder={`${currencySymbol}X.XX`}
                                                    value={adSetFormik.values.bid_amount}
                                                    onChange={(e) => {
                                                        const value = e.target.value;
                                                        adSetFormik.handleChange(e);

                                                        if (value) {
                                                        if (isBidCapMatch) {
                                                            adSetFormik.setFieldValue('bid_strategy', 'LOWEST_COST_WITH_BID_CAP');
                                                            adSetFormik.setFieldValue('is_incremental_attribution_enabled', false);
                                                        } else if (isCostCapMatch) {
                                                            adSetFormik.setFieldValue('bid_strategy', 'COST_CAP');
                                                            adSetFormik.setFieldValue('is_incremental_attribution_enabled', false);
                                                        }
                                                        } else {
                                                        adSetFormik.setFieldValue('bid_strategy', 'LOWEST_COST_WITHOUT_CAP');
                                                        }
                                                    }}
                                                    onBlur={adSetFormik.handleBlur}
                                                    borderLeftRadius={'md'}
                                                    size={'sm'}
                                                    onWheel={(e) => e.target.blur()}
                                                    />
                                                </Tooltip>
                                                <InputRightAddon height={'32px'} fontSize={'14px'}>
                                                    {currencyCode}
                                                </InputRightAddon>
                                                </InputGroup>

                                                {adSetFormik.touched.bid_amount && adSetFormik.errors.bid_amount && (
                                                <Text fontSize="sm" color="red.500">{adSetFormik.errors.bid_amount}</Text>
                                                )}
                                            </Flex>
                                            );
                                        })()}
                                    </>
                                    }

                                    {/* Conditionally render the bid strategy radio options if bid_amount has a value */}
                                    {hasBidStrategy ? 
                                        <>
                                            {(adSetFormik.values.bid_amount) && (
                                                <Flex direction="column" mt={4}>
                                                    <Text mb={2} fontWeight="bold">Bid strategy</Text>
                                                    <RadioGroup
                                                        name="bid_strategy"
                                                        value={adSetFormik.values.bid_strategy}
                                                        onChange={(value) => adSetFormik.setFieldValue('bid_strategy', value)}
                                                    >

                                                        <Stack direction="row" spacing={4}>
                                                            <Flex direction="row" align="center" spacing={2}>
                                                                <Radio colorScheme='brand' value="LOWEST_COST_WITH_BID_CAP" />
                                                                <Flex direction="column" ml={2}>
                                                                    <Text fontWeight="bold" fontSize="14px">{bidStrategy === 'LOWEST_COST_WITH_BID_CAP' ? 'maximum bid in each auction' : 'Cost per result goal'}{" "}</Text>
                                                                    {bidStrategy ? null : <Text fontSize="12px" color="gray.500">Best for getting the most volume</Text>}
                                                                </Flex>
                                                            </Flex>
                                                        </Stack>
                                                    </RadioGroup>
                                                </Flex>
                                            )}
                                        </>
                                    : 
                                    <>
                                        {adSetFormik.values.optimization_goal === 'QUALITY_CALL' ? (
                                            <Flex direction={'column'} gap={2}>
                                                <Text fontWeight="bold" fontSize={'14px'}>Highest volume</Text>
                                                <Text fontSize={'12px'}>
                                                    Meta will aim to spend your entire budget and get the most ad recall using the highest-volume bid strategy. This is the only bid strategy available for this campaign.
                                                </Text>
                                            </Flex>
                                        ) : (adSetFormik.values.bid_amount) && (
                                            <Flex direction="column" mt={4}>
                                                <Text mb={2} fontWeight="bold">Bid strategy</Text>
                                                <RadioGroup
                                                    name="bid_strategy"
                                                    value={adSetFormik.values.bid_strategy}
                                                    onChange={(value) => adSetFormik.setFieldValue('bid_strategy', value)}
                                                    >
                                                    <Stack direction="column" spacing={4}>
                                                        {/* COST_CAP Option */}
                                                        {adSetFormik.values.optimization_goal === 'IMPRESSIONS' ? null :
                                                            <Flex direction="row" align="center">
                                                                <Radio colorScheme="brand" value="COST_CAP" />
                                                                <Flex direction="column" ml={2}>
                                                                <Text fontWeight="bold" fontSize="14px">Cost per result goal</Text>
                                                                <Text fontSize="12px" color="gray.500">Best for getting the most volume</Text>
                                                                </Flex>
                                                            </Flex>
                                                        }
                                                        
                                                        {/* LOWEST_COST_WITH_BID_CAP Option */}
                                                        <Flex direction="row" align="center">
                                                        <Radio colorScheme="brand" value="LOWEST_COST_WITH_BID_CAP" />
                                                        <Flex direction="column" ml={2}>
                                                            <Text fontWeight="bold" fontSize="14px">Bid cap</Text>
                                                            <Text fontSize="12px" color="gray.500">Best for controlling bids in the auction</Text>
                                                        </Flex>
                                                        </Flex>
                                                    </Stack>
                                                </RadioGroup>
                                            </Flex>
                                        )}
                                    </>
                                    }
                                </Flex>

                                {/* Conditional ROAS Amount Field */}
                                <Flex direction="column">
                                    {adSetFormik.values.optimization_goal &&
                                        bid_strategy?.option[2]?.optimization_goal.includes(adSetFormik.values.optimization_goal) && (
                                            <Flex direction="column">
                                                <Flex alignItems={'center'}>
                                                    <Text mb={2} fontWeight="bold">
                                                        {bidStrategy === 'LOWEST_COST_WITH_MIN_ROAS' ? 'ROAS goal' : 'ROAS amount'}{" "}
                                                        {bidStrategy ? null : <Text as="span" fontWeight="normal">Optional</Text>}
                                                    </Text>
                                                    {bidStrategy ?
                                                        <Tooltip
                                                            borderRadius={5}
                                                            label={
                                                                <Box fontSize="sm" p={'5px'}>
                                                                    <Text>
                                                                        Meta will try to get the most purchase value and to keep the average ROAS (return on ad spend) around 0.010. Some results may be lower and some may be higher.
                                                                    </Text>
                                                                </Box>
                                                            }
                                                            fontSize="md"
                                                        >
                                                            <InfoOutlineIcon pointerEvents="all" ml={2} mb={2} />
                                                        </Tooltip>
                                                        : null}
                                                </Flex>

                                                <InputGroup>
                                                    <Input
                                                        name="roas_average_floor"
                                                        type="number"
                                                        placeholder={`${currencySymbol}X.XX`}
                                                        value={adSetFormik.values.roas_average_floor}
                                                        onChange={(e) => {
                                                            const value = e.target.value;
                                                            adSetFormik.handleChange(e);
                                                            if (value) {
                                                                adSetFormik.setFieldValue('bid_strategy', 'LOWEST_COST_WITH_MIN_ROAS');
                                                            } else {
                                                                adSetFormik.setFieldValue('bid_strategy', 'LOWEST_COST_WITHOUT_CAP');
                                                            }
                                                        }}
                                                        onBlur={adSetFormik.handleBlur}
                                                        borderRight="0"
                                                        size={'sm'}
                                                        onWheel={(e) => e.target.blur()}
                                                        borderRadius={'md'}
                                                    />
                                                    <InputRightAddon height={'32px'} fontSize={'14px'}>
                                                        {currencyCode}
                                                    </InputRightAddon>
                                                </InputGroup>

                                                {adSetFormik.touched.roas_average_floor && adSetFormik.errors.roas_average_floor && (
                                                    <Text fontSize={'sm'} color="red.500">{adSetFormik.errors.roas_average_floor}</Text>
                                                )}
                                            </Flex>
                                        )}
                                    {/* Conditionally render the bid strategy radio options if roas_average_floor has a value */}
                                    {adSetFormik.values.roas_average_floor && (adSetFormik.values.optimization_goal == "VALUE") && (
                                        <Flex direction="column" mt={4}>
                                            <Text mb={2} fontWeight="bold">Bid strategy</Text>
                                            <RadioGroup
                                                name="bid_strategy"
                                                value={adSetFormik.values.bid_strategy}
                                                onChange={(value) => adSetFormik.setFieldValue('bid_strategy', value)}
                                            >
                                                <Stack direction="row" spacing={4}>
                                                    <Flex direction="row" align="center">
                                                        <Radio colorScheme="brand" value="LOWEST_COST_WITH_MIN_ROAS" />
                                                        <Flex direction="column" ml={2}>
                                                            <Text fontWeight="bold" fontSize="14px">Cost per ROAS</Text>
                                                        </Flex>
                                                    </Flex>
                                                </Stack>
                                            </RadioGroup>
                                        </Flex>
                                    )}
                                </Flex>

                                <Box>
                                    <Divider my={2} />
                                        <Box mt={4}>
                                            <Flex alignItems="center" justifyContent="space-between">
                                                <Flex flexDirection={'column'} gap={2}>
                                                    <Button onClick={() => setDeliveryTypeExpanded(!isDeliveryTypeExpanded)} variant="link" rightIcon={isDeliveryTypeExpanded ? <ChevronUpIcon color={textColor} /> : <ChevronDownIcon color={textColor} />}>
                                                        <Text fontSize={'14px'} color={textColor}>{isDeliveryTypeExpanded ? 'Hide Options' : 'Show More Options'}</Text>
                                                    </Button>
                                                </Flex>
                                            </Flex>
                                        </Box>
                                        <Collapse in={isDeliveryTypeExpanded}>
                                            {/* Attribution setting Field */}
                                            {(adSetFormik.values.optimization_goal === 'OFFSITE_CONVERSIONS' || adSetFormik.values.optimization_goal === 'VALUE') && adSetFormik.values.destination_type !== 'APP' ? (
                                                <Flex direction="column" gap={2} pt={4}>
                                                    <Flex align={'center'} mt={'10px'}>
                                                        <Text fontWeight="bold">Attribution setting</Text>
                                                        <Box>
                                                            <Tooltip
                                                                borderRadius={5}
                                                                label={
                                                                    <Box fontSize="12px" p={'5px'}>
                                                                        <Text textAlign={'justify'}>
                                                                            The attribution setting is a finite period of time during which conversions can be credited to your ads and used to inform campaign optimisation. For example, with 1-day engaged-view, our system will learn from conversions that happen within 1 day and show ads to people most likely to convert in a day. Multiple attribution settings can be selected for reporting. You can see more details when you use Compare attribution settings in the campaign table.
                                                                        </Text>
                                                                    </Box>
                                                                }
                                                                fontSize="md"
                                                            >
                                                                <InfoOutlineIcon pointerEvents="all" ml={2} />
                                                            </Tooltip>
                                                        </Box>
                                                    </Flex>
                                                    <Flex>
                                                        <RadioGroup
                                                            name="is_incremental_attribution_enabled"
                                                            onChange={(value) => {
                                                                const isIncremental = value === 'true';
                                                                adSetFormik.setFieldValue('is_incremental_attribution_enabled', isIncremental);
                                                        
                                                                if (isIncremental) {
                                                                    adSetFormik.setFieldValue('attribution_spec', [
                                                                        { event_type: 'CLICK_THROUGH', window_days: 7 },
                                                                        { event_type: 'VIEW_THROUGH', window_days: 1 },
                                                                        { event_type: 'ENGAGED_VIDEO_VIEW', window_days: 1 },
                                                                    ]);
                                                                }
                                                            }}
                                                            value={adSetFormik.values.is_incremental_attribution_enabled ? 'true' : 'false'}
                                                        >
                                                            <Stack spacing={2} direction="column" mb={2}>
                                                                {/* Standard Attribution */}
                                                                <Box>
                                                                    <Flex align="center">
                                                                        <Radio colorScheme="brand" value="false">
                                                                            <Text fontSize="14px" fontWeight="medium">Standard attribution</Text>
                                                                        </Radio>
                                                                    </Flex>
                                                                    <Text fontSize="12px" pl="25px">
                                                                        Optimize delivery for a selected time window and interaction type. You can use the default or edit this below.
                                                                    </Text>
                                                                </Box>

                                                                {/* Incremental Attribution */}
                                                                <Box>
                                                                    <Flex align="center">
                                                                        <Radio colorScheme="brand" value="true" isDisabled={!!adSetFormik.values.bid_amount}>
                                                                            <Text fontSize="14px" fontWeight="medium">Incremental attribution</Text>
                                                                        </Radio>
                                                                    </Flex>
                                                                    <Text fontSize="12px" pl="25px" color={!!adSetFormik.values.bid_amount ? 'gray.400' : 'none'}>
                                                                        Optimize delivery for incremental conversions using sophisticated models that predict whether a conversion is caused by an ad.
                                                                    </Text>
                                                                </Box>
                                                            </Stack>
                                                        </RadioGroup>
                                                    </Flex>
                                                    {adSetFormik.values.is_incremental_attribution_enabled ? null :
                                                    <>
                                                        <Text fontSize={'14px'} fontWeight={'bold'}>Standard attribution selections</Text>
                                                        <AttributeSetting
                                                            setFieldValue={adSetFormik.setFieldValue}
                                                            attribution_spec={adSetFormik.values.attribution_spec}
                                                            optimization_goal={adSetFormik.values.optimization_goal}
                                                            destination_type={adSetFormik.values.destination_type}
                                                        />
                                                    </>
                                                    }
                                                </Flex>
                                            ) : (null)}

                                            {["LINK_CLICKS"].includes(adSetFormik.values.optimization_goal) && ['APP', 'WHATSAPP'].includes(adSetFormik?.values?.destination_type)  ? 
                                                <Flex direction="column" gap={2}>
                                                    <Flex align={'center'} mt={'10px'}>
                                                        <Text fontWeight="bold">When you are charged</Text>
                                                        <Tooltip
                                                            borderRadius={5}
                                                            label={
                                                                <Flex direction={'column'} fontSize="12px" p={'5px'}>
                                                                    <Text>Determines when you pay for your ad.</Text>
                                                                    <Text>For many conversion goals, you'll pay per impression (each time that your ad is served).</Text>
                                                                    <Text>For some conversion goals, you can pay per impression or per specific action (such as link clicks or ThruPlay).</Text>
                                                                </Flex>
                                                            }
                                                            fontSize="md"
                                                        >
                                                            <InfoOutlineIcon pointerEvents="all" ml={2} />
                                                        </Tooltip>
                                                    </Flex>
                                                    <Flex gap={2} px={1}>
                                                        <Select
                                                            name="billing_event"
                                                            value={adSetFormik?.values?.billing_event}
                                                            onChange={(e) => adSetFormik.setFieldValue('billing_event', e.target.value)}
                                                            onBlur={adSetFormik.handleBlur}
                                                            borderRadius={'md'}
                                                            size={'sm'}
                                                        >
                                                        
                                                            <option value={'IMPRESSIONS'}>Impression</option>
                                                            <option value={'LINK_CLICKS'} disabled={adSetFormik.values.bid_strategy === 'COST_CAP'}>Link Click (CPC)</option>
                                                        </Select>
                                                    </Flex>
                                                </Flex>
                                            :null}
                                        </Collapse>
                                </Box>
                            </Flex>

                            {/* Bid Multiplier */}
                            <Flex bg={backgroundColor} p={4} flexDirection="column" borderRadius="md" gap={4} mt={4}>
                                <Flex
                                    gap={{ base: 2, md: 3 }}
                                    align="center"
                                    flexWrap="wrap"
                                    >
                                    <Text fontWeight="bold">
                                        Bid Multiplier
                                    </Text>

                                    <Tooltip
                                        borderRadius="md"
                                        maxW="300px"
                                        placement="right"
                                        label={
                                        <Box fontSize="12px" p={2}>
                                            <Text textAlign="justify">
                                                Bid multipliers enable advertisers to reduce the amount of audience
                                                segmentation required because it allows them to maintain a nuanced
                                                bidding strategy within a single ad set with one targeted audience. 
                                                For example, advertisers typically create ad sets so that they can 
                                                bid differently for various audience segments. For example, an 
                                                advertiser can create 3 ad sets corresponding to 3 different age 
                                                groups: 18–25, 26–35, 36–65. With bid multiplier, an advertiser can 
                                                create a single ad set, then use bid multipliers to bid differently 
                                                for each of those buckets.
                                            </Text>
                                        </Box>
                                        }
                                        fontSize="md"
                                        hasArrow
                                    >
                                        <InfoOutlineIcon cursor="pointer" />
                                    </Tooltip>

                                    <Link
                                        href="https://developers.facebook.com/docs/marketing-api/bidding-and-optimization/bid-multiplier/"
                                        isExternal
                                        fontSize={{ base: "xs", md: "sm" }}
                                        color={inputTextColor}
                                        whiteSpace="nowrap"
                                    >
                                        (Learn More About Facebook Bid Multipliers)
                                    </Link>
                                </Flex>
                                <BidMultiplier adsAccountId={firstAdAccountId} preFilledData={''} onBidChange={handleBidChange} />
                            </Flex>

                            {/* Budget Scheduling */}
                            <Flex bg={backgroundColor} p={4} flexDirection="column" borderRadius="md" gap={4} mt={4}>
                                <Text fontWeight="bold">Budget & schedule</Text>
                                {/* Budget */}
                                <Flex alignItems={'center'}>
                                    <Text fontWeight="bold">{budget.label}</Text>
                                    <Tooltip
                                        borderRadius={5}
                                        label={
                                            <Box fontSize="sm" p={'5px'}>
                                                <Text>
                                                    Your ad set budget is the daily or lifetime amount that you want to spend on this ad set.
                                                    A daily budget is the average that you'll spend every day. A lifetime budget is the maximum that you'll spend during the lifetime of your ad set.
                                                </Text>
                                            </Box>
                                        }
                                        fontSize="md"
                                    >
                                        <InfoOutlineIcon pointerEvents="all" ml={2} />
                                    </Tooltip>
                                </Flex>
                                {lifetimeBudget || dailyBudget ? (
                                    <>
                                        {lifetimeBudget ? (
                                            <Text fontSize={'14px'}>You set a lifetime Advantage campaign budget of {currencySymbol}{lifetimeBudget / 100}.</Text>
                                        ) : null}

                                        {dailyBudget ? (
                                            <Text fontSize={'14px'}>You set a daily Advantage campaign budget of {currencySymbol}{dailyBudget / 100}.</Text>
                                        ) : null}
                                    </>
                                ) :
                                    <Flex>
                                        <Select
                                            name="budget"
                                            value={budgetType}
                                            onChange={handleBudgetChange}
                                            onBlur={adSetFormik.handleBlur}
                                            bg="#fff"
                                            borderRadius={'md'}
                                            borderRight={'none'}
                                            borderRightRadius="0"
                                            maxW={'200px'}
                                            size={'sm'}
                                        >
                                            {budget.options?.map((option) => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </Select>
                                        {budgetType === 'daily_budget' && (
                                            <InputGroup>
                                                <Input
                                                    name="daily_budget"
                                                    placeholder={currencySymbol}
                                                    type="number"
                                                    value={String(adSetFormik.values.daily_budget)}
                                                    onChange={(e) => adSetFormik.setFieldValue('daily_budget', e.target.value)}
                                                    onBlur={adSetFormik.handleBlur}
                                                    borderRight="0"
                                                    borderLeftRadius="none"
                                                    size={'sm'}
                                                    onWheel={(e) => e.target.blur()}
                                                />
                                                <InputRightAddon height={'32px'} fontSize={'14px'}>
                                                    {currencyCode}
                                                </InputRightAddon>
                                            </InputGroup>
                                        )}
                                        {budgetType === 'lifetime_budget' && (
                                            <InputGroup>
                                                <Input
                                                    name="lifetime_budget"
                                                    placeholder={currencySymbol}
                                                    type="number"
                                                    value={adSetFormik.values.lifetime_budget}
                                                    onChange={(e) => adSetFormik.setFieldValue('lifetime_budget', e.target.value)}
                                                    onBlur={adSetFormik.handleBlur}
                                                    borderRight="0"
                                                    borderLeftRadius="none"
                                                    size={'sm'}
                                                    onWheel={(e) => e.target.blur()}
                                                />
                                                <InputRightAddon height={'32px'} fontSize={'14px'}>
                                                    {currencyCode}
                                                </InputRightAddon>
                                            </InputGroup>
                                        )}
                                    </Flex>
                                }
                                {adSetFormik.touched.budget && adSetFormik.errors.budget && (
                                    <Text fontSize={'sm'} color="red.500">{adSetFormik.errors.budget}</Text>
                                )}
                                {adSetFormik.values.budget === 'daily_budget' && adSetFormik.touched.daily_budget && adSetFormik.errors.daily_budget && (
                                    <Text fontSize={'sm'} color="red.500">{adSetFormik.errors.daily_budget}</Text>
                                )}
                                {adSetFormik.values.budget === 'lifetime_budget' && adSetFormik.touched.lifetime_budget && adSetFormik.errors.lifetime_budget && (
                                    <Text fontSize={'sm'} color="red.500">{adSetFormik.errors.lifetime_budget}</Text>
                                )}

                                {/* Schedule */}
                                <Flex alignItems={'center'} mt={2}>
                                    <Text fontWeight="bold">Schedule</Text>
                                    <Tooltip
                                        borderRadius={5}
                                        label={
                                            <Box fontSize="sm" p={'5px'}>
                                                <Text>
                                                    You can choose to run your ads continuously starting today or only during a specific date range.
                                                </Text>
                                            </Box>
                                        }
                                        fontSize="md"
                                    >
                                        <InfoOutlineIcon pointerEvents="all" ml={2} />
                                    </Tooltip>
                                </Flex>

                                <Flex direction="column" gap={2}>
                                    {/* Start Date */}
                                    <Text fontWeight={'bold'}>Start date</Text>
                                    <Flex alignItems="center" gap={6}>
                                        <Box flex="1" border="1px solid" borderColor={borderColor} _hover={{ borderColor: hoverBorderColor }} borderRadius="md" p={1} bg={inputBackground}>
                                            <Flex alignItems="center" gap={2}>
                                                <CalendarIcon color={inputTextColor} boxSize={3.5} ml={2} />
                                                <DatePicker
                                                    selected={startDate}
                                                    onChange={(date) => setStartDate(date)}
                                                    minDate={toZonedTime(new Date(), timeZone)}
                                                    dateFormat="dd/MM/yyyy"
                                                    timeFormat="HH:mm"
                                                    timeIntervals={15}
                                                    timeCaption="Time"
                                                    customInput={
                                                        <input
                                                            type="text"
                                                            className="datepicker-input"
                                                            placeholder={''}
                                                            style={{
                                                                fontSize: '13px',
                                                                padding: '4px',
                                                                border: 'none',
                                                                width: '190px',
                                                                height: '18px',
                                                                color: inputTextColor,
                                                                backgroundColor: 'inherit',
                                                            }}
                                                        />
                                                    }
                                                />
                                            </Flex>
                                        </Box>
                                        <Box flex="1" border="1px solid" borderColor={borderColor} _hover={{ borderColor: hoverBorderColor }} borderRadius="md" p={'4px'} bg={inputBackground}>
                                            <Flex alignItems="center" gap={2}>
                                                <TimeIcon color={inputTextColor} boxSize={3.5} ml={2}/>
                                                <MinuteSecondSpinner value={startTime} onChange={(newTime) => setStartTime(newTime)} />
                                                <Text fontSize="12px" color={inputTextColor}>{timeZoneAbbreviation}</Text>
                                            </Flex>
                                        </Box>
                                    </Flex>

                                    {/* End Date */}
                                    {(adSetFormik.values.budget === 'daily_budget' || adSetFormik.values.budget === 'lifetime_budget') && (
                                        <Flex direction="column" gap={2} mt={4}>
                                            {adSetFormik.values.budget === 'daily_budget' && (
                                                <Checkbox
                                                    colorScheme='brand'
                                                    isChecked={showEndDate}
                                                    onChange={() => setShowEndDate(!showEndDate)}
                                                >
                                                    Set an end date
                                                </Checkbox>
                                            )}
                                            {(adSetFormik.values.budget === 'lifetime_budget' || showEndDate) && (
                                                <>
                                                    <Text fontWeight={'bold'}>End date</Text>
                                                    <Flex alignItems="center" gap={6}>
                                                        <Box flex="1" border="1px solid" borderColor={borderColor}  _hover={{ borderColor: hoverBorderColor }} borderRadius="md" p={1} bg={inputBackground}>
                                                            <Flex alignItems="center" gap={2}>
                                                                <CalendarIcon color={inputTextColor} boxSize={3.5} ml={2} />
                                                                <DatePicker
                                                                    selected={endDate}
                                                                    onChange={(date) => setEndDate(date)}
                                                                    minDate={toZonedTime(new Date(), timeZone)}
                                                                    dateFormat="dd/MM/yyyy"
                                                                    timeFormat="HH:mm"
                                                                    timeIntervals={15}
                                                                    timeCaption="Time"
                                                                    customInput={
                                                                        <input
                                                                            type="text"
                                                                            className="datepicker-input"
                                                                            placeholder={''}
                                                                            style={{
                                                                                color: inputTextColor,
                                                                                fontSize: '13px',
                                                                                padding: '4px',
                                                                                border: 'none',
                                                                                width: '190px',
                                                                                height: '18px',
                                                                                backgroundColor: 'inherit',
                                                                            }}
                                                                        />
                                                                    }
                                                                />
                                                            </Flex>
                                                        </Box>
                                                        <Box flex="1" border="1px solid" borderColor={borderColor}  _hover={{ borderColor: hoverBorderColor }} borderRadius="md" p={'4px'} bg={inputBackground}>
                                                            <Flex alignItems="center" gap={2}>
                                                                <TimeIcon color={inputTextColor} boxSize={3.5} ml={2}/>
                                                                <MinuteSecondSpinner value={endTime} onChange={(newTime) => setEndTime(newTime)} />
                                                                <Text fontSize="12px" color={inputTextColor}>{timeZoneAbbreviation}</Text>
                                                            </Flex>
                                                        </Box>
                                                    </Flex>
                                                    <Flex>
                                                        {errorDateMessage && <Text fontSize={'12px'} color="red.500">{errorDateMessage}</Text>}
                                                    </Flex>
                                                </>
                                            )}
                                        </Flex>
                                    )}
                                </Flex>

                                <Button justifyContent={'flex-start'} onClick={() => setIsSchedulingExpanded(!isSchedulingExpanded)} variant="link" rightIcon={isSchedulingExpanded ? <ChevronUpIcon color={textColor} /> : <ChevronDownIcon color={textColor} />}>
                                    <Text fontSize={'14px'} color={textColor}>{isSchedulingExpanded ? 'Hide Options' : 'Show More Options'}</Text>
                                </Button>

                                {/* Collapsible content */}
                                <Collapse in={isSchedulingExpanded}>
                                    {hasBidStrategy && isScheduled ?
                                        <Flex direction={'column'} gap={2}>
                                            <Flex alignItems={'center'}>
                                                <Text fontWeight="bold">Ad scheduling</Text>
                                                <Tooltip
                                                    borderRadius={5}
                                                    label={
                                                        <Box fontSize="sm" p={'5px'}>
                                                            <Text>
                                                                You can schedule your ads to deliver on specific hours and days of the week based on the viewer's or your ad account's time zone.
                                                            </Text>
                                                        </Box>
                                                    }
                                                    fontSize="md"
                                                >
                                                    <InfoOutlineIcon pointerEvents="all" ml={2} />
                                                </Tooltip>
                                            </Flex>
                                            <Flex>
                                                <Tooltip
                                                    borderRadius={5}
                                                    p={2}
                                                    ml={20}
                                                    label="Ad scheduling is only available when you use lifetime budget."
                                                    isDisabled={adSetFormik.values.budget === 'lifetime_budget'}
                                                >
                                                    <span>
                                                        <Checkbox
                                                            colorScheme='brand'
                                                            isChecked={isScheduled}
                                                            onChange={(e) => setIsScheduled(e.target.checked)}
                                                            isDisabled={adSetFormik.values.budget !== 'lifetime_budget'}
                                                        >
                                                            Run ads on a schedule
                                                        </Checkbox>
                                                    </span>
                                                </Tooltip>
                                            </Flex>
                                        </Flex>
                                    : null}

                                    {!hasBidStrategy ?
                                        <Flex direction={'column'} gap={4}>
                                            <Flex direction={'column'} gap={2}>
                                                <Flex direction={'column'}>
                                                    <Text fontWeight="bold">Budget scheduling</Text>
                                                    <Text fontSize={'12px'}>Increase your budget during specific days or times.</Text>
                                                </Flex>
                                                <Flex>
                                                    <Tooltip
                                                        borderRadius={5}
                                                        p={2}
                                                        ml={20}
                                                        placement='top'
                                                        fontSize={'sm'}
                                                        label="Your high-demand periods are turned off and will not run until you enable budget scheduling."
                                                    >
                                                        <span>
                                                            <Checkbox
                                                                colorScheme={"brand"}
                                                                name="is_budget_schedule_enabled"
                                                                size={'md'}
                                                                isChecked={adSetFormik.values.is_budget_schedule_enabled}
                                                                onChange={adSetFormik.handleChange}
                                                                isDisabled={adSetFormik.values.budget !== 'daily_budget'}
                                                            >
                                                                Schedule budget increases
                                                            </Checkbox>
                                                        </span>
                                                    </Tooltip>
                                                </Flex>
                                                {adSetFormik?.values?.is_budget_schedule_enabled && adSetFormik?.values?.budget === 'daily_budget' ? 
                                                    <BudgetScheduleSpecs
                                                        specs={adSetFormik?.values?.budget_schedule_specs}
                                                        setFieldValue={adSetFormik?.setFieldValue}
                                                        currency={adsAccountData?.currency}
                                                    />
                                                :null}
                                            </Flex>

                                            <Flex direction={'column'} gap={2}>
                                                <Flex alignItems={'center'}>
                                                    <Text fontWeight="bold">Ad scheduling</Text>
                                                    <Tooltip
                                                        borderRadius={5}
                                                        label={
                                                            <Box fontSize="sm" p={'5px'}>
                                                                <Text>
                                                                    You can schedule your ads to deliver on specific hours and days of the week based on the viewer's or your ad account's time zone.
                                                                </Text>
                                                            </Box>
                                                        }
                                                        fontSize="md"
                                                    >
                                                        <InfoOutlineIcon pointerEvents="all" ml={2} />
                                                    </Tooltip>
                                                </Flex>

                                                <Flex>
                                                    <Tooltip
                                                        borderRadius={5}
                                                        p={2}
                                                        ml={20}
                                                        label="Ad scheduling is only available when you use lifetime budget."
                                                        isDisabled={adSetFormik.values.budget === 'lifetime_budget'}
                                                    >
                                                        <span>
                                                            <Checkbox
                                                                colorScheme='brand'
                                                                isChecked={isScheduled}
                                                                onChange={(e) => setIsScheduled(e.target.checked)}
                                                                isDisabled={adSetFormik.values.budget !== 'lifetime_budget'}
                                                            >
                                                                Run ads on a schedule
                                                            </Checkbox>
                                                        </span>
                                                    </Tooltip>
                                                </Flex>
                                            </Flex>
                                        </Flex>
                                    : 
                                        <Flex direction={'column'} gap={2}>
                                            <Flex alignItems={'center'}>
                                                <Text fontWeight="bold">Budget scheduling</Text>
                                                <Tooltip
                                                    borderRadius={5}
                                                    label={
                                                        <Box fontSize="sm" p={'5px'}>
                                                            <Text>
                                                                To view or edit budget scheduling, go to campaign setup.
                                                            </Text>
                                                        </Box>
                                                    }
                                                    fontSize="md"
                                                >
                                                    <InfoOutlineIcon pointerEvents="all" ml={2} />
                                                </Tooltip>
                                            </Flex>
                                            <Text fontSize={'14px'}>You entered your high-demand periods using Advantage campaign budget</Text>
                                        </Flex>
                                    }

                                    {isScheduled && adSetFormik.values.budget === 'lifetime_budget' && (
                                        <>
                                            <Flex mb={4} direction={'column'} gap={2}>
                                                <Select
                                                    value={timezone}
                                                    onChange={(e) => setTimezone(e.target.value)}
                                                    size={'sm'}
                                                    borderRadius="md"
                                                >
                                                    <option value="USER">Use viewer's time zone</option>
                                                    <option value="ADVERTISER">Use this ad account's time zone</option>
                                                </Select>
                                                {timezone == "USER" && <Text fontSize={'12px'}>We'll schedule your ads based on the time zone of the person seeing it.</Text>}
                                                {timezone == "ADVERTISER" && <Text fontSize={'12px'}>We'll schedule your ads based on ad account's time zone</Text>}
                                            </Flex>
                                            <AdScheduler onAdScheduleChange={handleAdScheduleChange} adsetScheduleData={[]} />
                                            {(pacing_type && pacing_type.includes('day_parting') && !(adSchedule.length > 0)) ?
                                                <Flex alignItems="center" gap={2} className="error-class-schedule-time">
                                                    <InfoOutlineIcon pointerEvents="all" ml={2} color={'red.500'} />
                                                    <Text fontSize={'sm'} color="red.500">Schedule time is required</Text>
                                                </Flex>
                                            : null}
                                        </>
                                    )}
                                </Collapse>
                            </Flex>

                            {/* Audience controls  */}
                            <Flex bg={backgroundColor} p={4} flexDirection="column" borderRadius="md" gap={2} mt={4} overflow="visible" zIndex={1000} >
                                <Flex alignItems={'center'}>
                                    <Text fontWeight="bold">Audience controls</Text>
                                    <Tooltip
                                        borderRadius={5}
                                        label={
                                            <Box fontSize="sm" p={'5px'}>
                                                <Text>
                                                    <strong>Setting audience controls </strong><br></br>
                                                    With Advantage+ audience, our system searches within these criteria for a more specific audience. To give our system flexibility to get results, we recommend setting these controls to create a broad audience.
                                                </Text>
                                            </Box>
                                        }
                                        fontSize="md"
                                    >
                                        <InfoOutlineIcon pointerEvents="all" ml={2} />
                                    </Tooltip>
                                </Flex>
                                {/* Targeting */}
                                <Flex flexDirection="column">
                                    <Flex alignItems={'center'}>
                                        <Text fontWeight="bold">{targeting.geo_locations.label}</Text>
                                        <Text display="inline-flex" fontSize="18px" color="red.400">*</Text>
                                        <Tooltip
                                            borderRadius={5}
                                            label={
                                                <Box fontSize="sm" p={'5px'}>
                                                    <Text>
                                                        Choosing a broad area to show your ads within can improve results. For example, by adding a country instead of several cities.
                                                    </Text>
                                                </Box>
                                            }
                                            fontSize="md"
                                        >
                                            <InfoOutlineIcon pointerEvents="all" ml={2} />
                                        </Tooltip>
                                    </Flex>
                                    <Text fontSize={'14px'}>Reach people living in or recently in this location.</Text>
                                    <GeoLocationsUpdateSelector
                                        preFilledExcludedLocations={''}
                                        preFilledIncludedLocations={''}
                                        onLocationsChange={handleLocationsChange}
                                        onBlur={adSetFormik.handleBlur}
                                        selectedZipCodes={zipCodes}
                                        adsAccountData={campaignData?.adsAccount?.adsAccountId}
                                    />
                                    {adSetFormik.touched.geo_locations && adSetFormik.errors.geo_locations && (
                                        <Text fontSize={'sm'} color="red.500">{adSetFormik.errors.geo_locations}</Text>
                                    )}
                                </Flex>
                                <Link fontWeight={'bold'} fontSize={'xs'} textDecoration={'underline'} onClick={() => setisBulkLocationSelectorVisible(true)}>Add locations in bulk</Link>
                                <BulkLocationSelector
                                    isVisible={isBulkLocationSelectorVisible}
                                    onClose={() => setisBulkLocationSelectorVisible(false)}
                                    onSubmit={(zips) => {
                                        setzipCodes(zips)
                                        let selectedLocations = adSetFormik.values.geo_locations
                                        adSetFormik.setFieldValue('geo_locations', [...new Set([...selectedLocations, ...zips])]);
                                    }}
                                    adsAccountId={campaignData?.adsAccount?.adsAccountId}
                                />
                                <Divider />

                                {/* Age Gender & Detailed targeting */}
                                <Box>
                                    <Box>
                                        <Flex flexDirection={'column'} gap={2}>
                                            <Text fontWeight={'bold'}>Audience</Text>

                                            {/* Custom Audiences */}
                                            <Flex direction="column" gap={2}>
                                                <Flex align={'center'} >
                                                    <Text fontWeight="bold">Custom Audiences</Text>
                                                    <Box>
                                                        <Tooltip
                                                            borderRadius={5}
                                                            label={
                                                                <Box fontSize="12px" p={'5px'}>
                                                                    <Text textAlign={'justify'}>
                                                                        Include a custom audience to reach people who've already interacted with your business or lookalike audiences to reach new people similar to your existing customers.
                                                                    </Text>
                                                                </Box>
                                                            }
                                                            fontSize="md"
                                                        >
                                                            <InfoOutlineIcon pointerEvents="all" ml={2} />
                                                        </Tooltip>
                                                    </Box>
                                                </Flex>
                                                <Text fontSize={'12px'}>
                                                    <strong>Include</strong> people who are in at least <strong>one</strong> of the following
                                                </Text>
                                                <IncludeCustomAudienceWithBotNullifier
                                                    preFilledTargets={''}
                                                    adsAccount={{
                                                        adsAccountId: adsAccountData?.account_id,
                                                        adsAccountName: adsAccountData?.name,
                                                    }}
                                                    promoted_object={adSetFormik?.values?.promoted_object || []}
                                                    onIncludeAudienceChange={handleIncludeCustomAudienceChange}
                                                />

                                                <Flex direction={'column'} gap={2}>
                                                    {showExludeAudience ? null :
                                                        <Flex>
                                                            <Button size={'sm'} bg={'none'} border={'1px solid gray'} onClick={() => setshowExludeAudience(true)}>
                                                                Add exclusions
                                                            </Button>
                                                        </Flex>
                                                    }
                                                    {showExludeAudience ?
                                                        <>
                                                            <Text fontSize={'12px'}>
                                                                <strong>Exclude</strong> people who are in at least <strong>one</strong> of the following
                                                            </Text>
                                                            <ExcludeCustomAudience preFilledTargets={''} adsAccountId={firstAdAccountId} onExcludeAudienceChange={handleExcludeCustomAudienceChange} />
                                                        </>
                                                        : null}
                                                </Flex>
                                            </Flex>
                                            <Button justifyContent={'flex-start'} onClick={() => setIsExpanded(!isExpanded)} variant="link" rightIcon={isExpanded ? <ChevronUpIcon color={textColor} /> : <ChevronDownIcon color={textColor} />}>
                                                <Text fontSize={'14px'} color={textColor}>{isExpanded ? 'Hide Options' : 'Show More Options'}</Text>
                                            </Button>
                                        </Flex>
                                    </Box>

                                    {/* Collapsible content */}
                                    <Collapse in={isExpanded}>
                                        <Flex direction="column" gap={4} pt={4}>
                                            {/* Audience + */}
                                            {showAdvantage ? null : (
                                                <Flex direction="column">
                                                    <Card borderLeft="4px solid green">
                                                        <Flex gap={2} p={3} direction={'column'}>
                                                            <Flex gap={2}>
                                                                <SlGraph color='green' />
                                                                <Text fontSize="12px" fontWeight={'bold'}>
                                                                    You could get 33% lower cost per result with Advantage+ audience
                                                                </Text>
                                                                <Tooltip
                                                                    borderRadius={5}
                                                                    label={
                                                                        <Box fontSize="12px" p={'5px'}>
                                                                            <Text >
                                                                                This result is based on an experiment run between March 2023 and June 2023. The experiment compared the results of ad sets delivered using Advantage+ audience versus the original audience for 16 advertisers. This is provided to give you an idea of possible performance, but performance isn't guaranteed.
                                                                            </Text>
                                                                        </Box>
                                                                    }
                                                                    fontSize="md"
                                                                >
                                                                    <InfoOutlineIcon pointerEvents="all" />
                                                                </Tooltip>
                                                            </Flex>
                                                            <Flex px={4} direction={'column'}>
                                                                <Text fontSize={'12px'}>
                                                                    Let our advanced ad technology automatically find the audience for this campaign to help improve performance. You can still set limits to control who can and can't see your ads.
                                                                </Text>
                                                                <Flex py={2}>
                                                                    <Button size={'sm'} bg={'none'} border={'1px solid gray'} onClick={() => setShowAdvantage(true)}>
                                                                        Use Advantage+ audience
                                                                    </Button>
                                                                </Flex>
                                                            </Flex>
                                                        </Flex>
                                                    </Card>
                                                </Flex>
                                            )}

                                            <Flex direction="column">
                                                <Text fontWeight="bold">Detailed targeting</Text>
                                                <DetailedTargetingSelector preFilledTargets={''} adsAccounts={adsAccount} onInterestsChange={handleInterestsChange} adsAccountId={campaignData?.adsAccount?.adsAccountId} />
                                            </Flex>
                                            <AgeGenderSelectorForCampaigns
                                                onChange={handleAgeGenderChange}
                                                showAdvantageAudience={showAdvantage}
                                                ageRange={adSetFormik.values.ageRange}
                                            />
                                            <Box>
                                                <Flex align={'center'} mt={'10px'}>
                                                    <Text fontWeight="bold">Languages</Text>
                                                    <Box>
                                                        <Tooltip
                                                            borderRadius={5}
                                                            label={
                                                                <Box fontSize="sm" p={'5px'}>
                                                                    <Text>
                                                                        Enter a language to show your ads to people who use a language that isn't common to your location. Otherwise, leave this blank.
                                                                    </Text>
                                                                </Box>
                                                            }
                                                            fontSize="md"
                                                        >
                                                            <InfoOutlineIcon pointerEvents="all" ml={2} />
                                                        </Tooltip>
                                                    </Box>
                                                </Flex>
                                                <LocaleUpdateSelector
                                                    adsAccountId={campaignData?.adsAccount?.adsAccountId}
                                                    preFilledLocales={''}
                                                    onLocalesChange={(locales) => adSetFormik.setFieldValue('locales', locales)}
                                                />
                                            </Box>
                                            {showAdvantage ? (
                                                <Box>
                                                    <Divider />
                                                    <Flex py={'10px'} direction={'column'} gap={2}>
                                                        <Box display={'flex'} direction={'row'} gap={2} alignItems={'center'}>
                                                            <Text fontWeight="bold">Advantage+ audience</Text>
                                                            <BsStars />
                                                        </Box>
                                                        <Text fontSize={'12px'}>Our AI finds audiences for your ads. If you add an audience suggestion, you'll guide our AI towards people that you think are likely to respond. </Text>
                                                    </Flex>
                                                    <AdvantagePlusAudience preFilledData={''} ageOriginal={adSetFormik.values.ageOriginal} onChange={handleAgeRangeChange} showAdvantage={showAdvantage} />
                                                    <Divider my={2} />
                                                    <Flex justifyContent={'flex-end'}>
                                                        <Link fontWeight={'bold'} fontSize={'sm'} textDecoration={'underline'} onClick={() => setShowAdvantage(false)}>Switch to original audience</Link>
                                                    </Flex>
                                                </Box>
                                            ) : null}
                                        </Flex>
                                    </Collapse>
                                </Box>
                            </Flex>

                            {/* Placements Section */}
                            <Flex bg={backgroundColor} flexDirection="column" borderRadius="md" mt={4}>
                                <PlacementsSelector
                                    currentAdsetData={''}
                                    optimization_goal={adSetFormik.values.optimization_goal}
                                    objective={objective}
                                    buying_type={buying_type}
                                    billing_event={adSetFormik.values.billing_event}
                                    destination_type={adSetFormik.values.destination_type}
                                    onPlacementsChange={setPlacements}
                                    account_id={firstAdAccountId}
                                    adsAccountData={campaignData?.adsAccount?.adsAccountId}
                                />
                                <Box px={4}>
                                    <Divider />
                                </Box>
                                <Box p={4}>
                                    <Button onClick={() => setIsDeviceExpanded(!isDeviceExpanded)} variant="link" rightIcon={isDeviceExpanded ? <ChevronUpIcon color={textColor} /> : <ChevronDownIcon color={textColor} />}>
                                         <Text fontSize={'14px'} color={textColor}>{isDeviceExpanded ? 'Hide Options' : 'Show More Options'}</Text>
                                    </Button>
                                </Box>

                                <Box px={2}>
                                    <Collapse in={isDeviceExpanded}>
                                        <Flex direction={'column'} px={2} pb={4} gap={4}>
                                            <Flex direction='column' gap={2}>
                                                <Text fontWeight={'bold'} fontSize={'13px'}>Specific mobile devices & operating systems</Text>
                                                <Select
                                                    name=""
                                                    value={adSetFormik.values.app_store}
                                                    borderRadius="md"
                                                    size={'sm'}
                                                    onChange={(e) => adSetFormik.setFieldValue('app_store', e.target.value)}
                                                >
                                                    <option value="All">All mobile devices</option>
                                                    <option value="google_play">Android devices only</option>
                                                    <option value="itunes">iOS devices only</option>
                                                </Select>
                                            </Flex>

                                            {adSetFormik.values.app_store !== 'All' ?
                                              <>
                                                <Flex direction='column' gap={2}>
                                                    <Box>
                                                        <Text fontWeight={'bold'} fontSize={'14px'}>Included devices</Text>
                                                        <IncludeDevice preFilledTargets={''} app_store={adSetFormik.values.app_store} ExcludedUserDevice={adSetFormik?.values?.excluded_user_device} adsAccountId={firstAdAccountId} onIncludeDeviceChange={handleIncludeDeviceChange} />
                                                    </Box>

                                                    <Flex direction={'column'} gap={2}>
                                                        {showExludeDevices ? null :
                                                            <Flex>
                                                                <Button size={'sm'} bg={'none'} border={'1px solid gray'} onClick={() => setshowExludeDevices(true)}>
                                                                    Exclude Devices
                                                                </Button>
                                                            </Flex>
                                                        }
                                                        {showExludeDevices ?
                                                            <Box>
                                                                <Text fontWeight={'bold'} fontSize={'14px'}>Excluded devices</Text>
                                                                <ExcludeDevice preFilledTargets={''} app_store={adSetFormik.values.app_store} userDevice={adSetFormik?.values?.user_device} adsAccountId={firstAdAccountId} onIncludeDeviceChange={handleExcludeDeviceChange} />
                                                            </Box>
                                                            : null}
                                                    </Flex>
                                                </Flex>

                                                <Flex direction='column' gap={2}>
                                                    <Text fontWeight={'bold'} fontSize={'14px'}>OS versions</Text>
                                                    <OsVersions AppStore={adSetFormik.values.app_store} onOsVersionChange={handleOsVersionChange} />
                                                </Flex>
                                              </>
                                            :null }

                                            <Flex direction={'column'} gap={1}>
                                                  <Checkbox
                                                      size={'md'}
                                                      colorScheme='brand'
                                                      isChecked={adSetFormik?.values?.wireless_carrier?.length > 0}
                                                      onChange={() => {
                                                          if (adSetFormik?.values?.wireless_carrier?.length > 0) {
                                                              // If checked, empty the array
                                                              adSetFormik.setFieldValue('wireless_carrier', []);
                                                          } else {
                                                              // If unchecked, set to Wifi
                                                              adSetFormik.setFieldValue('wireless_carrier', ['Wifi']);
                                                          }
                                                      }}
                                                  >
                                                      <Flex>
                                                          <Text fontSize='14px'>Only when connected to Wi-Fi</Text>
                                                      </Flex>
                                                  </Checkbox>
                                            </Flex>

                                            <Flex direction={'column'} gap={1}>
                                              <Text fontWeight={'bold'} fontSize={'12px'} color={textColor}>Exclude skippable ads</Text>
                                                <Text fontSize={'12px'}  mb={1}>Available for <strong>in-stream videos.</strong></Text>
                                                <Checkbox
                                                    size={'md'}
                                                    colorScheme='brand'
                                                    isChecked={adSetFormik.values.instream_video_skippable_excluded}
                                                    onChange={() => adSetFormik.setFieldValue('instream_video_skippable_excluded', !adSetFormik.values.instream_video_skippable_excluded)}
                                                >
                                                    <Flex>
                                                        <Text fontSize='12px' fontWeight={'bold'}>Exclude skippable ads in the ad set (only for in-stream videos)</Text>
                                                    </Flex>
                                                </Checkbox>
                                            </Flex>
                                        </Flex>
                                    </Collapse>
                                </Box>
                            </Flex>

                            {/* Submit Button */}
                            <Flex justify="flex-start" my={'20px'} gap={2}>
                                <Button 
                                    colorScheme={'gray'}
                                    variant={'solid'}
                                    size={'sm'}
                                    onClick={onClose}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    colorScheme="brand"
                                    isLoading={adSetFormik.isSubmitting}
                                    onClick={handleSave}
                                    size={'sm'} >Save</Button>
                            </Flex>
                        </form>
                    </Box>
                </Flex>
                <Flex flex={0.4} direction="column" position="sticky" top="0" maxHeight="75vh" overflowY="auto">
                    <Audience adsAccount={campaignData?.adsAccount?.adsAccountId} promoted_object={adSetFormik?.values?.promoted_object || []} targeting_spec={buildTargetingSpec} optimizationGoal={adSetFormik.values?.optimization_goal} />
                </Flex>
            </Flex>
        </Box>
    );
}

export default CreateSalesForm;
